import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  User, Mail, Phone, Building2, GraduationCap, MapPin, Calendar,
  FileText, Trash2, Clock, History,
  X, Link, Download, ExternalLink, Send, Edit3, Check, Globe,
  ChevronLeft, ChevronRight, Mic, MicOff, Sparkles, Square, RefreshCw, ChevronDown, Search,
  Star, Share2, Loader2
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getCandidate, updateCandidate, updateCandidateStage, deleteCandidate,
  addCandidateTag, removeCandidateTag, createTag, createEvaluation, deleteEvaluation,
  notifyReview, getMentionableUsers, getFreeBusy, rescheduleInterview, getMeetingRooms, getFeishuMeetingRooms, getRoomFreebusy,
  getScreeningRecords, createScreeningRecord,
  getInterviewRecords, createInterviewRecord, cancelInterview, addInterviewInterviewer, removeInterviewInterviewer, nudgeInterviewEvaluation,
  getSalaryMaterials, uploadSalaryMaterials, deleteSalaryMaterial,
  getOfferApprovals,
    getReminders, createReminder, deleteReminder, updateEvaluation, getEvaluationHistory, aiAnalyzeResume, aiGenerateEvaluation,
    getApplicationMaterials, createApplicationMaterial, deleteApplicationMaterial, getResumeSnapshot, uploadResume,
    favoriteCandidate, unfavoriteCandidate, sendCandidateEmail } from '../api';
import OfferDecisionPanel from './OfferDecisionPanel';
import PdfViewer from './PdfViewer';
import InterviewCodeOfConductModal from './InterviewCodeOfConductModal';
import { getInterviewConduct } from '../api';
import { Candidate, Position, Tag, User as UserType, STAGE_LABELS, PIPELINE_STAGES, MAIN_FLOW_STAGES, CandidateStage } from '../types';
import { formatBeijingTime } from '../utils/dateFormat';
import { isTouchPrimary } from '../utils/device';

// 触屏设备（手机/平板）的 iframe 无法内嵌渲染 PDF，整个生命周期内保持 PDF.js 高清渲染
const TOUCH_PRIMARY = isTouchPrimary();

interface Props {
  candidateId: string;
  positions: Position[];
  allTags: Tag[];
  currentUser: UserType;
  onUpdate: () => void;
  onTagsChange: () => void;
  onBack: () => void;
  candidateIds?: string[];
  onNavigate?: (id: string) => void;
}

// 单蓝色系：进行中阶段蓝色由浅到深表示推进，淘汰/终止用中性灰
const stageColors: Record<string, string> = {
  new: 'bg-gray-400',
  screening: 'bg-primary-400',
  screening_passed: 'bg-primary-500',
  screening_rejected: 'bg-gray-400',
  first_interview: 'bg-primary-500',
  first_interview_rejected: 'bg-gray-400',
  second_interview: 'bg-primary-500',
  second_interview_rejected: 'bg-gray-500',
  final_interview: 'bg-primary-600',
  final_interview_rejected: 'bg-gray-500',
  offer: 'bg-primary-600',
  offer_rejected: 'bg-gray-500',
  pending_onboard: 'bg-primary-600',
  onboarded: 'bg-primary-700',
};

function getRatingLevel(v: number): { label: string; color: string; bg: string } {
  // 单蓝色系：分数越高蓝越深；待观察=中性灰；淘汰=近黑；未评分=浅灰
  if (v >= 9) return { label: '卓越', color: 'text-primary-800', bg: 'bg-primary-50' };
  if (v >= 8) return { label: '强烈推荐', color: 'text-primary-700', bg: 'bg-primary-50' };
  if (v > 7.5) return { label: '推荐', color: 'text-primary-600', bg: 'bg-primary-50' };
  if (v >= 7) return { label: '通过', color: 'text-primary-600', bg: 'bg-primary-50' };
  if (v >= 6) return { label: '待观察', color: 'text-gray-600', bg: 'bg-gray-100' };
  if (v > 0) return { label: '淘汰', color: 'text-gray-900', bg: 'bg-gray-100' };
  return { label: '未评分', color: 'text-gray-400', bg: 'bg-gray-50' };
}

function getRatingTrackColor(v: number): string {
  // 分数越高蓝越深；待观察=灰；淘汰=近黑；未评分=浅灰
  if (v >= 9) return '#1a43ad';
  if (v >= 8) return '#1f54d6';
  if (v > 7.5) return '#2e6bf0';
  if (v >= 7) return '#2e6bf0';
  if (v >= 6) return '#667085';
  if (v > 0) return '#101828';
  return '#cbd2df';
}

/**
 * 岗位选择器的候选岗位：只能改到「招聘中」的岗位，草稿/暂停/关闭都不列出。
 * 当前已关联的岗位始终保留，否则候选人挂在已暂停岗位上时选择器里看不到当前值。
 */
function pickablePositions(positions: Position[], currentId: string | null | undefined, search: string): Position[] {
  const q = search.trim().toLowerCase();
  return positions.filter(p =>
    (p.status === 'open' || p.id === currentId) && p.name.toLowerCase().includes(q)
  );
}

/** 解析候选人所属岗位配置的默认面试官 user id 列表（供发起评审自动带出） */
function positionInterviewerIds(positions: Position[], positionId: string | null | undefined): string[] {
  if (!positionId) return [];
  const pos = positions.find(p => p.id === positionId);
  if (!pos?.interviewer_ids) return [];
  try {
    const arr = JSON.parse(pos.interviewer_ids);
    return Array.isArray(arr) ? arr.filter((x: any) => typeof x === 'string' && x) : [];
  } catch { return []; }
}

export default function CandidateDetail({ candidateId, positions, allTags, currentUser, onUpdate, onTagsChange, onBack, candidateIds, onNavigate }: Props) {
  // Navigation: prev/next candidate
  const currentIdx = candidateIds ? candidateIds.indexOf(candidateId) : -1;
  const prevId = candidateIds && currentIdx > 0 ? candidateIds[currentIdx - 1] : null;
  const nextId = candidateIds && currentIdx >= 0 && currentIdx < candidateIds.length - 1 ? candidateIds[currentIdx + 1] : null;
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [evalComment, setEvalComment] = useState('');
  const [evalRating, setEvalRating] = useState(0);
  const [evalPrivate, setEvalPrivate] = useState(false);
  const [stampAnimation, setStampAnimation] = useState<'pass' | 'reject' | null>(null);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [rightTab, setRightTab] = useState<'notes' | 'followup'>('notes');
  const [editingInfo, setEditingInfo] = useState(false);
  const [editFields, setEditFields] = useState<Record<string, any>>({});
  const [sendingFeishu, setSendingFeishu] = useState(false);
  const [showFeishuPicker, setShowFeishuPicker] = useState(false);
  const [showEmailPicker, setShowEmailPicker] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  // 邮件发送弹窗
  const [emailModalType, setEmailModalType] = useState<'interview_invite' | 'rejection' | null>(null);
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [feishuInterviewers, setFeishuInterviewers] = useState<UserType[]>([]);
  // Searchable position picker (assign position)
  const [showPositionPicker, setShowPositionPicker] = useState(false);
  const [positionSearch, setPositionSearch] = useState('');
  const positionPickerRef = useRef<HTMLDivElement | null>(null);
  const [leftTab, setLeftTab] = useState<'resume' | 'screening' | 'interview' | 'offer_decision' | 'application'>('resume');
  const [screeningRecords, setScreeningRecords] = useState<any[]>([]);
  const [interviewRecords, setInterviewRecords] = useState<any[]>([]);
  // 拉分后待确认「作为哪一轮面评提交」的候选场次；null = 无需确认
  const [claimPrompt, setClaimPrompt] = useState<any[] | null>(null);
  const [claimChoice, setClaimChoice] = useState('');
  // 代交面评：本次评价归在哪位面试官名下，'' = 以本人身份提交
  const [claimBehalfOf, setClaimBehalfOf] = useState('');
  const [salaryMaterials, setSalaryMaterials] = useState<any[]>([]);
  const [offerApprovals, setOfferApprovals] = useState<any[]>([]);
  // Salary material upload
  const [salaryFiles, setSalaryFiles] = useState<File[]>([]);
  const [salaryNote, setSalaryNote] = useState('');
  const [uploadingSalary, setUploadingSalary] = useState(false);
  const [allUsers, setAllUsers] = useState<UserType[]>([]);
  // Screening form
  const [scrResult, setScrResult] = useState('passed');
  const [scrComment, setScrComment] = useState('');
  // Interview form
  const [showInterviewForm, setShowInterviewForm] = useState(false);
  const [ivInterviewerIds, setIvInterviewerIds] = useState<string[]>([]);
  const [ivRound, setIvRound] = useState('初试');
  const [ivStartTime, setIvStartTime] = useState('');
  const [ivEndTime, setIvEndTime] = useState('');
  const [ivLocation, setIvLocation] = useState('');
  const [ivType, setIvType] = useState<'video' | 'onsite'>('video');
  const [ivMeetingRoomId, setIvMeetingRoomId] = useState('');
  const [meetingRooms, setMeetingRooms] = useState<any[]>([]);
  const [meetingRoomSource, setMeetingRoomSource] = useState<'feishu' | 'local'>('local');
  const [meetingRoomHint, setMeetingRoomHint] = useState('');
  const [roomBusyMap, setRoomBusyMap] = useState<Record<string, {start: string; end: string}[]>>({});
  // Freebusy
  const [busySlots, setBusySlots] = useState<{start: string; end: string}[]>([]);
  const [busyHint, setBusyHint] = useState('');
  const [loadingBusy, setLoadingBusy] = useState(false);
  // Edit interview (full edit)
  const [editingInterviewId, setEditingInterviewId] = useState<string | null>(null);
  const [editIvStartTime, setEditIvStartTime] = useState('');
  const [editIvEndTime, setEditIvEndTime] = useState('');
  const [editIvLocation, setEditIvLocation] = useState('');
  const [editIvType, setEditIvType] = useState<'video' | 'onsite'>('video');
  const [editIvRound, setEditIvRound] = useState('');
  const [editIvInterviewerId, setEditIvInterviewerId] = useState('');
  const [editIvMeetingRoomId, setEditIvMeetingRoomId] = useState('');
  const [cancelIvId, setCancelIvId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  // 拒Offer：填写拒绝原因备注，记入操作记录
  const [rejectOfferTarget, setRejectOfferTarget] = useState<null | { navigate: boolean }>(null);
  const [rejectOfferReason, setRejectOfferReason] = useState('');
  // Interview feedback
  const [feedbackInterviewId, setFeedbackInterviewId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [viewingHistoryEvalId, setViewingHistoryEvalId] = useState<string | null>(null);
  const [evaluationHistory, setEvaluationHistory] = useState<any[]>([]);
  // Application materials
  const [appMaterials, setAppMaterials] = useState<any[]>([]);
  const [showAppMaterialForm, setShowAppMaterialForm] = useState(false);
  const [appMatType, setAppMatType] = useState<'cover_letter' | 'recommendation' | 'attachment'>('cover_letter');
  const [appMatTitle, setAppMatTitle] = useState('');
  const [appMatContent, setAppMatContent] = useState('');
  const [appMatFile, setAppMatFile] = useState<File | null>(null);
  const [appMatRecName, setAppMatRecName] = useState('');
  const [appMatRecRelation, setAppMatRecRelation] = useState('');
  const [appMatRecContact, setAppMatRecContact] = useState('');
  // @mention in notes
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionInsertPos, setMentionInsertPos] = useState(0);
  const evalTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [mentionableUsers, setMentionableUsers] = useState<UserType[]>([]);
  const [mobileView, setMobileView] = useState<'left' | 'right'>('left');
  // 手机端候选信息卡默认折叠为一行摘要，把屏幕空间让给简历；桌面端不折叠（此状态不生效）
  const [mobileInfoExpanded, setMobileInfoExpanded] = useState(false);
  // Reminders
  const [reminders, setReminders] = useState<any[]>([]);
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [reminderTargetId, setReminderTargetId] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [reminderContent, setReminderContent] = useState('');
  // AI resume analysis
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState<any>(null);
  const [updatingResume, setUpdatingResume] = useState(false);
  // 桌面默认浏览器原生 iframe 快速预览，发现缺字时手动切 PDF.js 高清；
  // 触屏设备（手机/平板）的 iframe 无法渲染 PDF，默认直接用 PDF.js 高清
  const [hdRender, setHdRender] = useState(TOUCH_PRIMARY);
  const resumeInputRef = useRef<HTMLInputElement | null>(null);
  // Voice input for evaluation
  const [isRecording, setIsRecording] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const recognitionRef = React.useRef<any>(null);
  const isHr = currentUser.role === 'admin' || currentUser.role === 'hr' || currentUser.role === 'vip';
  const selectableUsers = allUsers.filter(u => u.status === 'active' && u.role !== 'referrer');
  // Interview code of conduct modal (shown once per session for non-HR)
  const SESSION_KEY = 'interview_conduct_confirmed';
  const [showConductModal, setShowConductModal] = useState(false);
  const [conductContent, setConductContent] = useState('');

  // Show conduct modal once per session for non-HR roles
  useEffect(() => {
    if (isHr) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    getInterviewConduct()
      .then(data => { setConductContent(data.content || ''); setShowConductModal(true); })
      .catch(() => {});
  }, []);

  // Close position picker on outside click
  useEffect(() => {
    if (!showPositionPicker) return;
    const handler = (e: MouseEvent) => {
      if (positionPickerRef.current && !positionPickerRef.current.contains(e.target as Node)) {
        setShowPositionPicker(false);
        setPositionSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPositionPicker]);

  // Close 发送飞书/发送邮件 dropdowns on outside click
  useEffect(() => {
    if (!showFeishuPicker && !showEmailPicker) return;
    const handler = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setShowFeishuPicker(false);
        setShowEmailPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFeishuPicker, showEmailPicker]);

  const openEmailModal = (type: 'interview_invite' | 'rejection') => {
    setShowEmailPicker(false);
    if (!candidate?.email?.trim()) { toast.error('该候选人未填写邮箱，无法发送邮件'); return; }
    setEmailMessage('');
    setEmailModalType(type);
  };

  const handleSendEmail = async () => {
    if (!emailModalType) return;
    setSendingEmail(true);
    try {
      const res = await sendCandidateEmail(candidateId, {
        type: emailModalType,
        message: emailModalType === 'rejection' ? emailMessage : undefined,
      });
      toast.success(res.message || '邮件已发送');
      setEmailModalType(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '邮件发送失败');
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchDetail = useCallback(async () => {
    if (!candidateId) return;
    try {
      setLoading(true);
      const data = await getCandidate(candidateId);
      setCandidate(data);
      setIsFavorite(!!data.is_favorite);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || '加载失败';
      toast.error(`加载候选人详情失败: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // 切换候选人时，简历预览回到默认模式（桌面快速 / 触屏高清），手机信息卡回到折叠态
  useEffect(() => { setHdRender(TOUCH_PRIMARY); setMobileInfoExpanded(false); }, [candidateId]);

  const fetchReminders = useCallback(async () => {
    try { setReminders(await getReminders(candidateId)); } catch {}
  }, [candidateId]);

  useEffect(() => { if (rightTab === 'followup') fetchReminders(); }, [rightTab, fetchReminders]);

  const handleCreateReminder = async () => {
    if (!reminderTime || !reminderContent.trim() || !reminderTargetId) {
      toast.error('请填写完整提醒信息'); return;
    }
    try {
      await createReminder({
        candidate_id: candidateId,
        target_user_id: reminderTargetId,
        remind_at: reminderTime.replace('T', ' '),
        content: reminderContent.trim(),
      });
      toast.success('提醒已设置');
      setShowReminderForm(false);
      setReminderTime(''); setReminderContent(''); setReminderTargetId('');
      fetchReminders();
    } catch { toast.error('设置提醒失败'); }
  };

  const handleDeleteReminder = async (id: string) => {
    try {
      await deleteReminder(id);
      toast.success('提醒已取消');
      fetchReminders();
    } catch { toast.error('取消失败'); }
  };

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (cancelIvId) { setCancelIvId(null); return; } if (rejectOfferTarget) { setRejectOfferTarget(null); return; } onBack(); }
      // Left/Right arrow navigation (only when not typing in an input)
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft' && prevId && onNavigate) { e.preventDefault(); onNavigate(prevId); }
      if (e.key === 'ArrowRight' && nextId && onNavigate) { e.preventDefault(); onNavigate(nextId); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onBack, prevId, nextId, onNavigate, cancelIvId, rejectOfferTarget]);

  // Fetch tab data
  const fetchRecords = useCallback(async (tab: string) => {
    try {
      if (tab === 'screening') setScreeningRecords(await getScreeningRecords(candidateId));
      if (tab === 'interview') setInterviewRecords(await getInterviewRecords(candidateId));
      if (tab === 'offer_decision') {
        setSalaryMaterials(await getSalaryMaterials(candidateId));
        setOfferApprovals(await getOfferApprovals(candidateId));
      }
      if (tab === 'application') setAppMaterials(await getApplicationMaterials(candidateId));
    } catch {}
  }, [candidateId]);

  useEffect(() => { if (leftTab !== 'resume') fetchRecords(leftTab); }, [leftTab, fetchRecords]);

  // 右侧评分需要判断「我是否某场已开始面试的面试官」，所以面试记录不能只在切到面试 Tab 时才加载
  useEffect(() => {
    if (!candidateId) return;
    getInterviewRecords(candidateId).then(setInterviewRecords).catch(() => {});
  }, [candidateId]);

  useEffect(() => {
    getMentionableUsers().then(u => {
      setAllUsers(u.filter((user: UserType) => user.role !== 'referrer'));
      setMentionableUsers(u);
    }).catch(() => {});
    getFeishuMeetingRooms().then(data => {
      console.log('[MeetingRooms] Feishu response:', data?.source, data?.rooms?.length, 'rooms');
      setMeetingRooms(data.rooms || []);
      setMeetingRoomSource(data.source || 'local');
      if (data.hint) { setMeetingRoomHint(data.hint); toast(data.hint, { icon: '⚠️' }); }
    }).catch(err => {
      console.warn('[MeetingRooms] Feishu API failed, using local:', err?.message || err);
      getMeetingRooms().then(r => setMeetingRooms(r.map((rm: any) => ({ ...rm, room_id: '', floor_name: rm.location || '' })))).catch(() => {});
    });
  }, [candidateId]);

  // 可被本次评分「认领」的面试场次：面试已开始、未取消，且「我」或「我能代交的面试官」还没打分
  // 注意必须放在下面的提前 return 之前，Hook 不能条件调用
  const claimableInterviews = useMemo(() => {
    if (!currentUser) return [];
    const now = Date.now();
    const hrRole = currentUser.role === 'admin' || currentUser.role === 'hr';
    // 评价归属人 = 被代表的面试官（代交）或作者本人
    const ownerOf = (ev: any) => ev.on_behalf_of || ev.user_id;
    const scoredPairs = new Set(
      (candidate?.evaluations || [])
        .filter((ev: any) => ev.interview_record_id && ev.rating > 0)
        .map((ev: any) => `${ev.interview_record_id}:${ownerOf(ev)}`)
    );
    return interviewRecords
      .filter((r: any) => r.status !== 'cancelled')
      .filter((r: any) => r.scheduled_time && new Date(r.scheduled_time).getTime() <= now)
      // HR 可代任意面试官，面试官可代同场其他面试官；两者都要求这场至少还有人没打分
      .filter((r: any) => hrRole || (r.interviewers || []).some((iv: any) => iv.id === currentUser.id))
      .map((r: any) => ({
        ...r,
        // 这场还缺谁的面评：用于弹窗里的「以谁的身份提交」
        pendingInterviewers: (r.interviewers || []).filter((iv: any) => !scoredPairs.has(`${r.id}:${iv.id}`)),
      }))
      .filter((r: any) => r.pendingInterviewers.length > 0)
      .sort((a: any, b: any) => new Date(b.scheduled_time).getTime() - new Date(a.scheduled_time).getTime());
  }, [interviewRecords, candidate, currentUser]);

  if (loading || !candidate) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-white border-t-transparent"></div>
      </div>
    );
  }

  const currentStageIdx = PIPELINE_STAGES.indexOf(candidate.stage as CandidateStage);
  const isInFlow = !!candidate.position_id;

  const handleStageChange = async (newStage: string, reason?: string) => {
    try {
      await updateCandidateStage(candidate.id, newStage, reason);
      toast.success(`已移至: ${STAGE_LABELS[newStage as CandidateStage]}`);
      fetchDetail(); onUpdate();
    } catch (err: any) { 
      console.error('Stage change error:', err);
      if (err?.response?.status === 401) {
        toast.error('登录已过期，请重新登录');
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(err?.response?.data?.message || err?.message || '操作失败');
      }
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('确认删除该候选人？此操作不可恢复。')) return;
    try {
      await deleteCandidate(candidate.id);
      toast.success('已删除'); onUpdate(); onBack();
    } catch { toast.error('删除失败'); }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/?candidate=${candidate.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('候选人链接已复制到剪贴板');
    } catch {
      toast.error('复制失败，请手动复制');
    }
  };

  const handleToggleFavorite = async () => {
    if (favLoading) return;
    const next = !isFavorite;
    setIsFavorite(next); // optimistic
    setFavLoading(true);
    try {
      if (next) { await favoriteCandidate(candidate.id); toast.success('已收藏'); }
      else { await unfavoriteCandidate(candidate.id); toast.success('已取消收藏'); }
    } catch {
      setIsFavorite(!next); // revert on failure
      toast.error('操作失败');
    } finally {
      setFavLoading(false);
    }
  };


  const startEditInfo = () => {
    setEditFields({
      name: candidate.name || '',
      phone: candidate.phone || '',
      email: candidate.email || '',
      wechat: candidate.wechat || '',
      gender: candidate.gender || '',
      age: candidate.age || 0,
      work_years: candidate.work_years || 0,
      latest_company: candidate.latest_company || '',
      latest_title: candidate.latest_title || '',
      education: candidate.education || '',
      school: candidate.school || '',
      major: candidate.major || '',
      expected_city: candidate.expected_city || '',
      personal_website: candidate.personal_website || '',
      portfolio_url: candidate.portfolio_url || '',
    });
    setEditingInfo(true);
  };

  const saveEditInfo = async () => {
    try {
      await updateCandidate(candidate.id, editFields);
      toast.success('信息已更新');
      setEditingInfo(false);
      fetchDetail(); onUpdate();
    } catch { toast.error('更新失败'); }
  };

  const handleSendFeishu = async (interviewerIds: string[]) => {
    setSendingFeishu(true);
    try {
      const result = await notifyReview({
        candidate_ids: [candidate.id],
        interviewer_ids: interviewerIds,
      });
      
      // Update send frequency for sorting
      const sendFrequency = JSON.parse(localStorage.getItem('interviewer_send_frequency') || '{}');
      interviewerIds.forEach(id => {
        sendFrequency[id] = (sendFrequency[id] || 0) + 1;
      });
      localStorage.setItem('interviewer_send_frequency', JSON.stringify(sendFrequency));
      
      toast.success(result.message);
      setShowFeishuPicker(false);
      fetchDetail(); onUpdate(); // refresh to reflect auto stage change (new→screening)
    } catch (err: any) {
      toast.error(err.response?.data?.message || '发送失败');
    } finally {
      setSendingFeishu(false);
    }
  };

  const loadFeishuInterviewers = async () => {
    try {
      const users = await getMentionableUsers();
      const activeInterviewers = users.filter((u: UserType) => u.feishu_open_id && u.status === 'active' && u.role !== 'referrer');
      
      // Sort by send frequency (stored in localStorage)
      const sendFrequency = JSON.parse(localStorage.getItem('interviewer_send_frequency') || '{}');
      activeInterviewers.sort((a: UserType, b: UserType) => {
        const freqA = sendFrequency[a.id] || 0;
        const freqB = sendFrequency[b.id] || 0;
        return freqB - freqA; // Descending order (most frequent first)
      });
      
      setFeishuInterviewers(activeInterviewers);
    } catch {}
  };

  const handleAddScreening = async (overrideResult?: string) => {
    if (!candidate) return;
    const finalResult = overrideResult || scrResult;
    try {
      await createScreeningRecord({ candidate_id: candidate.id, result: finalResult, comment: scrComment });
      toast.success(finalResult === 'passed' ? '已通过，HR将收到通知' : finalResult === 'rejected' ? '已淘汰，HR将收到通知' : '筛选记录已添加');
      setScrComment('');
      setScreeningRecords(await getScreeningRecords(candidate.id));
    } catch { toast.error('添加失败'); }
  };

  // Helper: append +08:00 to datetime-local value for timezone safety
  const withTz = (dt: string) => {
    if (!dt) return dt;
    if (dt.includes('+') || dt.includes('Z')) return dt;
    return dt + '+08:00';
  };

  // Helper: compute duration in minutes between two datetime-local values
  const calcDuration = (start: string, end: string) => {
    if (!start || !end) return 60;
    return Math.round((new Date(withTz(end)).getTime() - new Date(withTz(start)).getTime()) / 60000);
  };

  // Format a UTC Date to Beijing datetime-local string (YYYY-MM-DDTHH:MM)
  const toBjLocal = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const bj = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Shanghai' }));
    return `${bj.getFullYear()}-${pad(bj.getMonth() + 1)}-${pad(bj.getDate())}T${pad(bj.getHours())}:${pad(bj.getMinutes())}`;
  };

  // Generate end-time slot options (30-min increments, up to 4 hours) from a start time
  const generateEndTimeSlots = (start: string): { value: string; label: string }[] => {
    if (!start) return [];
    const startMs = new Date(withTz(start)).getTime();
    const slots: { value: string; label: string }[] = [];
    for (const mins of [30, 45, 60, 90, 120]) {
      const endMs = startMs + mins * 60 * 1000;
      const endD = new Date(endMs);
      const bjEnd = new Date(endD.toLocaleString('en-US', { timeZone: 'Asia/Shanghai' }));
      const pad = (n: number) => String(n).padStart(2, '0');
      const timeLabel = `${pad(bjEnd.getHours())}:${pad(bjEnd.getMinutes())}`;
      const durLabel = mins < 60 ? `${mins} 分钟` : mins % 60 === 0 ? `${mins / 60} 小时` : `${(mins / 60).toFixed(1)} 小时`;
      slots.push({ value: toBjLocal(endD), label: `${timeLabel} (${durLabel})` });
    }
    return slots;
  };

  // Helper: auto-set end time when start changes — always use first slot value for select sync
  const handleStartTimeChange = (start: string, setEnd: (v: string) => void, currentEnd: string) => {
    if (!start) return;
    const slots = generateEndTimeSlots(start);
    if (!currentEnd || new Date(withTz(start)) >= new Date(withTz(currentEnd))) {
      // Default to 1-hour slot (index 1) or first slot
      setEnd(slots.length > 1 ? slots[1].value : slots[0]?.value || toBjLocal(new Date(new Date(withTz(start)).getTime() + 60 * 60 * 1000)));
    } else {
      // Snap currentEnd to nearest slot to prevent mismatch
      const curMs = new Date(withTz(currentEnd)).getTime();
      const startMs = new Date(withTz(start)).getTime();
      const diffMins = Math.round((curMs - startMs) / 60000);
      const slotIdx = Math.max(0, Math.min(slots.length - 1, Math.round(diffMins / 30) - 1));
      if (slots[slotIdx]) setEnd(slots[slotIdx].value);
    }
  };

  const handleScheduleInterview = async () => {
    if (!candidate || ivInterviewerIds.length === 0 || !ivStartTime) { toast.error('请填写完整信息'); return; }
    let duration = calcDuration(ivStartTime, ivEndTime);
    if (duration <= 0 || duration > 480) { duration = 60; } // safety clamp
    // Always recompute end_time from start + duration to ensure consistency
    const computedEnd = new Date(new Date(withTz(ivStartTime)).getTime() + duration * 60000).toISOString();
    try {
      const created = await createInterviewRecord({
        candidate_id: candidate.id, interviewer_ids: ivInterviewerIds,
        round: ivRound, scheduled_time: withTz(ivStartTime), end_time: computedEnd, duration_minutes: duration,
        location: ivType === 'onsite' ? ivLocation : '线上视频面试',
        interview_type: ivType, meeting_room_id: ivMeetingRoomId || '',
      });
      if ((created as any)?.feishu_calendar_error) {
        toast.error(`面试已安排，但飞书日程异常：${(created as any).feishu_calendar_error}`, { duration: 8000 });
      } else {
        toast.success(`面试已安排（${ivInterviewerIds.length}位面试官，共享同一日程）`);
      }
      setShowInterviewForm(false);
      setIvInterviewerIds([]); setIvStartTime(''); setIvEndTime(''); setIvLocation(''); setIvType('video'); setIvMeetingRoomId('');
      setInterviewRecords(await getInterviewRecords(candidate.id));
      setBusySlots([]); setBusyHint('');
    } catch (err: any) { toast.error(err.response?.data?.message || '安排失败'); }
  };

  // Fetch freebusy when interviewer is selected
  const fetchFreeBusy = async (userId: string) => {
    if (!userId) { setBusySlots([]); setBusyHint(''); return; }
    const user = allUsers.find(u => u.id === userId);
    if (!user?.feishu_open_id) { setBusySlots([]); setBusyHint('该用户未绑定飞书，无法查看闲忙'); return; }
    setLoadingBusy(true);
    try {
      const today = new Date();
      const start = today.toISOString().split('T')[0];
      const endDate = new Date(today); endDate.setDate(endDate.getDate() + 7);
      const end = endDate.toISOString().split('T')[0];
      const data = await getFreeBusy(userId, start, end);
      setBusySlots(data.busy_slots || []);
      setBusyHint(data.hint || '');
    } catch { setBusyHint('查询闲忙失败'); setBusySlots([]); }
    finally { setLoadingBusy(false); }
  };

  // Check room availability when time is selected
  const fetchRoomAvailability = async (time: string, duration: number) => {
    if (!time || meetingRoomSource !== 'feishu') { setRoomBusyMap({}); return; }
    try {
      const startDt = new Date(time);
      const endDt = new Date(startDt.getTime() + duration * 60 * 1000);
      // Query the whole day to show useful info
      const dayStart = time.split('T')[0] + 'T00:00:00+08:00';
      const dayEnd = time.split('T')[0] + 'T23:59:59+08:00';
      const data = await getRoomFreebusy(dayStart, dayEnd);
      setRoomBusyMap(data.busy_rooms || {});
    } catch { setRoomBusyMap({}); }
  };

  // Check if a room is busy at the given time
  const isRoomBusy = (roomId: string, time: string, duration: number): boolean => {
    const slots = roomBusyMap[roomId];
    if (!slots || slots.length === 0) return false;
    const start = new Date(time).getTime();
    const end = start + duration * 60 * 1000;
    return slots.some(s => {
      const bs = new Date(s.start).getTime();
      const be = new Date(s.end).getTime();
      return start < be && end > bs; // overlap check
    });
  };

  // Cancel interview — 打开带备注输入的确认弹窗
  const handleCancelInterview = (interviewId: string) => {
    if (!candidate) return;
    setCancelIvId(interviewId);
    setCancelReason('');
  };

  const confirmCancelInterview = async () => {
    if (!candidate || !cancelIvId) return;
    try {
      await cancelInterview(cancelIvId, cancelReason.trim());
      toast.success('面试已取消');
      setCancelIvId(null);
      setCancelReason('');
      setInterviewRecords(await getInterviewRecords(candidate.id));
    } catch (err: any) { toast.error(err.response?.data?.message || '取消失败'); }
  };

  // 确认标记为「拒Offer」：携带拒绝原因，记入操作记录
  const confirmRejectOffer = async () => {
    if (!rejectOfferTarget) return;
    const navigate = rejectOfferTarget.navigate;
    const reason = rejectOfferReason.trim();
    setRejectOfferTarget(null);
    setRejectOfferReason('');
    if (navigate) setStampAnimation('reject');
    await handleStageChange('offer_rejected', reason);
    if (navigate) {
      setTimeout(() => {
        setStampAnimation(null);
        if (nextId && onNavigate) onNavigate(nextId);
      }, 800);
    }
  };

  // Edit interview (full)
  const handleReschedule = async (interviewId: string) => {
    if (!candidate || !editIvStartTime) { toast.error('请填写面试时间'); return; }
    let duration = calcDuration(editIvStartTime, editIvEndTime);
    if (duration <= 0 || duration > 480) { duration = 60; } // safety clamp
    // Always recompute end_time from start + duration to ensure consistency
    const computedEnd = new Date(new Date(withTz(editIvStartTime)).getTime() + duration * 60000).toISOString();
    try {
      await rescheduleInterview(interviewId, {
        scheduled_time: withTz(editIvStartTime),
        end_time: computedEnd,
        duration_minutes: duration,
        location: editIvType === 'onsite' ? editIvLocation : '线上视频面试',
        interview_type: editIvType,
        round: editIvRound || undefined,
        meeting_room_id: editIvMeetingRoomId || '',
      });
      toast.success('面试已调整，飞书日程已同步更新');
      setEditingInterviewId(null);
      setInterviewRecords(await getInterviewRecords(candidate.id));
    } catch (err: any) { toast.error(err.response?.data?.message || '调整失败'); }
  };

  const handleAddTag = async (tagId: string) => {
    try { await addCandidateTag(candidate.id, tagId); fetchDetail(); onUpdate(); }
    catch { toast.error('添加标签失败'); }
  };

  const handleCreateAndAddTag = async () => {
    const name = newTagName.trim();
    if (!name) return;
    try {
      const colors = ['#2e6bf0','#1f54d6','#1a43ad','#5a8bfa','#8eb2ff','#344054','#667085','#98a2b3','#101828','#2e6bf0'];
      const color = colors[Math.floor(Math.random() * colors.length)];
      const tag = await createTag({ name, color });
      await addCandidateTag(candidate.id, tag.id);
      setNewTagName('');
      fetchDetail(); onUpdate(); onTagsChange();
      toast.success(`标签「${name}」已创建并添加`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '创建标签失败');
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try { await removeCandidateTag(candidate.id, tagId); fetchDetail(); onUpdate(); }
    catch { toast.error('移除标签失败'); }
  };

  // @ 名单以备注正文为唯一依据：提交时文本里@了谁就通知谁，删掉的@自然不再通知
  // （本组件在此之前有提前 return，不能用 useMemo）
  const parsedMentions: { id: string; name: string }[] = [];
  {
    // 长名字优先，否则「@张伟明」会把「张伟」也算成被@
    const candidates = mentionableUsers
      .map(u => ({ id: u.id, name: u.display_name || u.username }))
      .filter(c => c.name && c.id !== currentUser.id)
      .sort((a, b) => b.name.length - a.name.length);
    let rest = evalComment;
    for (const c of candidates) {
      if (!rest.includes(`@${c.name}`)) continue;
      parsedMentions.push(c);
      rest = rest.split(`@${c.name}`).join(' '); // 命中的片段不再参与后续匹配
    }
  }

  const submitEvaluation = async (interviewRecordId: string | null, behalfOf?: string) => {
    try {
      const target = interviewRecordId ? interviewRecords.find((r: any) => r.id === interviewRecordId) : null;
      const res = await createEvaluation({
        candidate_id: candidate.id,
        stage: target?.round || candidate.stage,
        ...(interviewRecordId ? { interview_record_id: interviewRecordId } : {}),
        ...(behalfOf ? { on_behalf_of: behalfOf } : {}),
        rating: evalRating, comment: evalComment.trim(),
        is_private: evalPrivate,
        mentioned_user_ids: parsedMentions.map(u => u.id),
      });
      setEvalComment(''); setEvalRating(0); setEvalPrivate(false);
      setClaimPrompt(null); setClaimChoice(''); setClaimBehalfOf('');
      // 按后端返回的流转结果给明确反馈，避免用户不知道评分触发了阶段变化
      const act = res?.stage_action;
      const who = res?.on_behalf_of_name ? `（代 ${res.on_behalf_of_name}）` : '';
      if (act?.type === 'advanced') toast.success(`面评已提交${who}，全部面试官均达 7 分，已流转至${act.next_label}`);
      else if (act?.type === 'rejected') toast.success(`面评已提交${who}，评分 ${act.rating} 分低于 6 分，已一票否决淘汰`);
      else if (act?.type === 'pending') toast.success(`面评已提交${who}，最低分 ${act.lowest} 分，本轮待定需 HR 决策`);
      else if (act?.type === 'waiting') toast.success(`面评已提交${who}，还有 ${act.waiting} 位面试官未评分`);
      else if (act?.type === 'passed_final') toast.success(`面评已提交${who}，本轮通过`);
      else toast.success('评价已添加');
      fetchDetail(); onUpdate();
      getInterviewRecords(candidate.id).then(setInterviewRecords).catch(() => {});
    } catch (err: any) { toast.error(err?.response?.data?.message || '添加评价失败'); }
  };

  const handleAddEvaluation = async () => {
    if (!evalComment.trim()) return;
    // 未拉分（rating = 0）= 纯备注，直接提交；拉了分且有可认领的场次 → 先确认轮次与身份
    if (evalRating > 0 && claimableInterviews.length > 0) {
      const first = claimableInterviews[0];
      setClaimChoice(first.id);
      // 默认身份：我本人就是这场待评面试官 → 以本人提交；否则（如 HR）默认代第一位待评面试官
      const meIsPending = (first.pendingInterviewers || []).some((iv: any) => iv.id === currentUser?.id);
      setClaimBehalfOf(meIsPending ? '' : (first.pendingInterviewers?.[0]?.id || ''));
      setClaimPrompt(claimableInterviews);
      return;
    }
    submitEvaluation(null);
  };

  // @mention: detect @ in textarea and show user picker
  const handleEvalCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setEvalComment(val);
    const cursorPos = e.target.selectionStart || 0;
    // Find the @ symbol before cursor
    const textBeforeCursor = val.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@([^\s@]*)$/);
    if (atMatch) {
      setShowMentionPicker(true);
      setMentionQuery(atMatch[1].toLowerCase());
      setMentionInsertPos(cursorPos - atMatch[1].length - 1); // position of @
    } else {
      setShowMentionPicker(false);
      setMentionQuery('');
    }
  };

  const handleMentionSelect = (user: UserType) => {
    const name = user.display_name || user.username;
    const before = evalComment.substring(0, mentionInsertPos);
    const cursorPos = evalTextareaRef.current?.selectionStart || mentionInsertPos;
    const after = evalComment.substring(cursorPos);
    const newComment = `${before}@${name} ${after}`;
    setEvalComment(newComment);
    setShowMentionPicker(false);
    setMentionQuery('');
    // Restore focus
    setTimeout(() => {
      const pos = before.length + name.length + 2; // after "@name "
      evalTextareaRef.current?.focus();
      evalTextareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const mentionFilteredUsers = mentionableUsers.filter(u =>
    u.id !== currentUser.id &&
    ((u.display_name || '').toLowerCase().includes(mentionQuery) ||
     (u.username || '').toLowerCase().includes(mentionQuery))
  );

  const handleDeleteEvaluation = async (evalId: string) => {
    if (!window.confirm('确认删除此评价？')) return;
    try {
      await deleteEvaluation(evalId);
      toast.success('评价已删除'); fetchDetail();
    } catch { toast.error('删除评价失败'); }
  };

  const handleFieldUpdate = async (field: string, value: any) => {
    try { await updateCandidate(candidate.id, { [field]: value }); fetchDetail(); onUpdate(); }
    catch { toast.error('更新失败'); }
  };

  const handleResumeFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !candidate) return;
    try {
      setUpdatingResume(true);
      const uploaded = await uploadResume(file);
      const resumeFile = uploaded?.file?.path;
      if (!resumeFile) throw new Error('上传失败');
      const updatePayload: any = { resume_file: resumeFile };
      if (uploaded?.resumeText) updatePayload.resume_text = uploaded.resumeText;
      await updateCandidate(candidate.id, updatePayload);
      toast.success('简历已更新');
      fetchDetail();
      onUpdate();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || '更新简历失败');
    } finally {
      setUpdatingResume(false);
    }
  };

  // Voice recording handlers
  const startVoiceRecording = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('当前浏览器不支持语音识别，请使用 Chrome');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'zh-CN';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    let finalText = voiceTranscript;

    recognition.onresult = (event: any) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += t;
        } else {
          interim = t;
        }
      }
      setVoiceTranscript(finalText + (interim ? `[${interim}]` : ''));
    };

    recognition.onerror = (event: any) => {
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        toast.error(`语音识别错误: ${event.error}`);
      }
      setIsRecording(false);
    };

    recognition.onend = () => {
      // Auto-restart if still recording (browser may stop after silence)
      if (recognitionRef.current && isRecording) {
        try { recognitionRef.current.start(); } catch {}
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
    toast.success('开始录音，请说话...');
  };

  const stopVoiceRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null; // prevent auto-restart
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsRecording(false);
    // Clean up interim markers
    setVoiceTranscript(prev => prev.replace(/\[.*?\]$/, ''));
  };

  const handleAiGenerateEval = async () => {
    if (!voiceTranscript.trim()) { toast.error('请先录入语音内容'); return; }
    if (!candidate) return;
    setAiGenerating(true);
    try {
      const cleanTranscript = voiceTranscript.replace(/\[.*?\]/g, '');
      const res = await aiGenerateEvaluation(candidate.id, cleanTranscript, candidate.stage || '');
      setEvalComment(res.evaluation || '');
      toast.success('AI面评已生成，可编辑后提交');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'AI面评生成失败');
    } finally {
      setAiGenerating(false);
    }
  };

  const availableTags = allTags.filter(t => !candidate.tags?.some(ct => ct.id === t.id));
  const resumeUrl = candidate.resume_file || '';
  const resumeImages: string[] = (() => {
    if (!resumeUrl) return [];
    if (resumeUrl.startsWith('[')) { try { return JSON.parse(resumeUrl); } catch { return []; } }
    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(resumeUrl)) return [resumeUrl];
    return [];
  })();
  const isPdf = resumeUrl?.toLowerCase().endsWith('.pdf');
  const isImage = resumeImages.length > 0;
  const ratingLevel = getRatingLevel(evalRating);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onBack} />

      {/* 通过/淘汰动画覆盖层 */}
      {stampAnimation && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center pointer-events-none">
          <div 
            className="animate-stamp-modern"
            style={{ animation: 'stampModern 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards' }}
          >
            {stampAnimation === 'pass' ? (
              <div className="relative">
                <div className="px-10 py-6 rounded-2xl border-4 border-primary-500 bg-primary-50/95 shadow-2xl backdrop-blur-sm transform -rotate-6">
                  <span className="text-6xl font-black text-primary-600 tracking-tight">
                    通过
                  </span>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div className="px-10 py-6 rounded-2xl border-4 border-gray-900 bg-gray-900/95 shadow-2xl backdrop-blur-sm transform rotate-6">
                  <span className="text-6xl font-black text-white tracking-tight">
                    淘汰
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Prev/Next navigation arrows */}
      {prevId && onNavigate && (
        <button
          onClick={() => onNavigate(prevId)}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-[60] w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center text-gray-500 hover:text-gray-800 transition-all"
          title="上一位候选人 (←)"
        >
          <ChevronLeft size={22} />
        </button>
      )}
      {nextId && onNavigate && (
        <button
          onClick={() => onNavigate(nextId)}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-[60] w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center text-gray-500 hover:text-gray-800 transition-all"
          title="下一位候选人 (→)"
        >
          <ChevronRight size={22} />
        </button>
      )}

      {/* Modal container */}
      <div className="relative w-full h-full md:w-[94vw] md:h-[92vh] max-w-[1600px] bg-white md:rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-modal-in">
        {/* Close button */}
        <button onClick={onBack}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/80 hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors shadow-sm">
          <X size={18} />
        </button>

        {/* Mobile view switcher（右侧留出关闭按钮空间，避免误触关闭） */}
        <div className="md:hidden shrink-0 flex border-b border-gray-200 bg-white pr-11">
          <button
            onClick={() => setMobileView('left')}
            className={clsx(
              'flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors',
              mobileView === 'left' ? 'text-primary-600 border-primary-600' : 'text-gray-400 border-transparent'
            )}
          >
            详情 / 简历
          </button>
          <button
            onClick={() => setMobileView('right')}
            className={clsx(
              'flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors',
              mobileView === 'right' ? 'text-primary-600 border-primary-600' : 'text-gray-400 border-transparent'
            )}
          >
            操作 / 备注
          </button>
        </div>

        {/* ===== 2-column layout (tab-switched on mobile) ===== */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">

          {/* ====== LEFT COLUMN ====== */}
          <div className={clsx(
            'flex-1 md:w-[75%] flex flex-col md:border-r border-gray-200 overflow-hidden',
            mobileView !== 'left' && 'hidden md:flex'
          )}>

            {/* LEFT-TOP: Candidate Info Card */}
            <div className="shrink-0 bg-white p-3 md:p-5 border-b border-gray-200">
              {/* 手机端摘要条：信息卡默认折叠，点击展开/收起；桌面端隐藏此条、信息卡常显 */}
              <button
                type="button"
                onClick={() => setMobileInfoExpanded(v => !v)}
                className="md:hidden w-full flex items-center gap-2 text-left"
              >
                <div className="shrink-0 w-8 h-8 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
                  {candidate.photo_url ? (
                    <img src={candidate.photo_url} alt={candidate.name} className="w-full h-full object-cover" />
                  ) : (
                    <User size={16} className="text-gray-400" />
                  )}
                </div>
                <span className="shrink-0 text-base font-bold text-gray-900 tracking-tight">{candidate.name}</span>
                <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-50 border border-gray-200 text-[11px] font-medium text-gray-600">
                  <span className={clsx('w-1.5 h-1.5 rounded-full', stageColors[candidate.stage] || 'bg-gray-400')} />
                  {STAGE_LABELS[candidate.stage as CandidateStage]}
                </span>
                {candidate.position_name && (
                  <span className="min-w-0 flex-1 truncate text-xs text-gray-400">{candidate.position_name}</span>
                )}
                <ChevronDown size={16} className={clsx('shrink-0 ml-auto text-gray-400 transition-transform duration-200', mobileInfoExpanded && 'rotate-180')} />
              </button>

              {/* 卡片完整内容：桌面用 md:contents 保持原有布局不变，手机端折叠时隐藏 */}
              <div className={clsx('mt-3 md:contents', !mobileInfoExpanded && 'hidden')}>
              {/* Position row at top */}
              <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3 text-xs flex-wrap">
                {candidate.source && <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500">{candidate.source === 'referral' ? '内推' : candidate.source}</span>}
                {candidate.referrer_name && <span className="px-2 py-0.5 rounded bg-primary-50 text-primary-600">内推码: {candidate.referrer_name}</span>}
                <div className="relative" ref={positionPickerRef}>
                  <button
                    type="button"
                    onClick={() => { setShowPositionPicker(v => !v); setPositionSearch(''); }}
                    className="h-7 flex items-center gap-1 rounded-lg border border-primary-200 bg-primary-50 px-2.5 text-sm font-medium text-primary-700 focus:outline-none focus:ring-1 focus:ring-primary-500 max-w-[220px]"
                  >
                    <span className="truncate">{candidate.position_name || '未分配岗位'}</span>
                    <ChevronDown size={14} className="shrink-0 text-primary-400" />
                  </button>
                  {showPositionPicker && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-30 py-1.5">
                      <div className="px-2 pb-1.5">
                        <div className="relative">
                          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            autoFocus
                            value={positionSearch}
                            onChange={e => setPositionSearch(e.target.value)}
                            placeholder="搜索岗位..."
                            className="w-full h-7 pl-7 pr-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => { handleFieldUpdate('position_id', null); setShowPositionPicker(false); setPositionSearch(''); }}
                          className={clsx(
                            'w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors',
                            !candidate.position_id ? 'text-primary-600 font-medium bg-primary-50' : 'text-gray-600'
                          )}
                        >
                          未分配岗位
                        </button>
                        {pickablePositions(positions, candidate.position_id, positionSearch)
                          .map(p => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => { handleFieldUpdate('position_id', p.id); setShowPositionPicker(false); setPositionSearch(''); }}
                              className={clsx(
                                'w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors truncate',
                                candidate.position_id === p.id ? 'text-primary-600 font-medium bg-primary-50' : 'text-gray-700'
                              )}
                            >
                              {p.name}
                            </button>
                          ))}
                        {pickablePositions(positions, candidate.position_id, positionSearch).length === 0 && (
                          <p className="px-3 py-2 text-xs text-gray-400 text-center">无匹配的招聘中岗位</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* Flow status merged into left header */}
                <span className={clsx(
                  'inline-flex items-center gap-1 h-7 px-2 rounded-lg text-xs font-medium border',
                  isInFlow ? 'bg-primary-50 text-primary-600 border-primary-200' : 'bg-gray-100 text-gray-500 border-gray-200'
                )}>
                  <span className={clsx('w-1.5 h-1.5 rounded-full', isInFlow ? 'bg-primary-500' : 'bg-gray-400')} />
                  {isInFlow ? '在流程中' : '在野'}
                </span>
                {/* Stage selector merged into left header */}
                {isHr ? (
                  <select
                    value={candidate.stage}
                    onChange={e => {
                      const target = e.target.value as CandidateStage;
                      if (target === candidate.stage) return;
                      if (target === 'offer_rejected') {
                        e.target.value = candidate.stage; // 受控回退，待弹窗确认后再切换
                        setRejectOfferReason('');
                        setRejectOfferTarget({ navigate: false });
                        return;
                      }
                      const fromLabel = STAGE_LABELS[candidate.stage as CandidateStage];
                      const toLabel = STAGE_LABELS[target];
                      if (window.confirm(`确认将阶段从「${fromLabel}」调整为「${toLabel}」？`)) {
                        handleStageChange(target);
                      } else {
                        e.target.value = candidate.stage;
                      }
                    }}
                    className="h-7 rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer"
                  >
                    {PIPELINE_STAGES.map(s => (
                      <option key={s} value={s}>{STAGE_LABELS[s]}</option>
                    ))}
                  </select>
                ) : (
                  <span className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-gray-50 border border-gray-200 text-xs font-medium text-gray-700">
                    <span className={clsx('w-1.5 h-1.5 rounded-full', stageColors[candidate.stage] || 'bg-gray-400')} />
                    {STAGE_LABELS[candidate.stage as CandidateStage]}
                  </span>
                )}
                <span className="text-gray-400 ml-auto">{formatBeijingTime(candidate.created_at, 'datetime')}</span>
              </div>
              <div className="flex items-start gap-3 md:gap-4">
                {/* Avatar */}
                <div className="shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
                  {candidate.photo_url ? (
                    <img src={candidate.photo_url} alt={candidate.name} className="w-full h-full object-cover" />
                  ) : (
                    <User size={28} className="text-gray-400" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {editingInfo ? (
                    /* ===== EDIT MODE ===== */
                    <div className="space-y-2.5">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <input value={editFields.name} onChange={e => setEditFields({...editFields, name: e.target.value})}
                          placeholder="姓名" className="col-span-1 h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <input value={editFields.gender} onChange={e => setEditFields({...editFields, gender: e.target.value})}
                          placeholder="性别" className="col-span-1 h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <input type="number" value={editFields.age || ''} onChange={e => setEditFields({...editFields, age: parseInt(e.target.value) || 0})}
                          placeholder="年龄" className="col-span-1 h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <input type="number" value={editFields.work_years || ''} onChange={e => setEditFields({...editFields, work_years: parseInt(e.target.value) || 0})}
                          placeholder="工作年限" className="col-span-1 h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input value={editFields.latest_company} onChange={e => setEditFields({...editFields, latest_company: e.target.value})}
                          placeholder="公司" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <input value={editFields.latest_title} onChange={e => setEditFields({...editFields, latest_title: e.target.value})}
                          placeholder="职位" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        <input value={editFields.school} onChange={e => setEditFields({...editFields, school: e.target.value})}
                          placeholder="学校" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <input value={editFields.education} onChange={e => setEditFields({...editFields, education: e.target.value})}
                          placeholder="学历" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <input value={editFields.major} onChange={e => setEditFields({...editFields, major: e.target.value})}
                          placeholder="专业" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <input value={editFields.phone} onChange={e => setEditFields({...editFields, phone: e.target.value})}
                          placeholder="手机号" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <input value={editFields.email} onChange={e => setEditFields({...editFields, email: e.target.value})}
                          placeholder="邮箱" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <input value={editFields.wechat || ''} onChange={e => setEditFields({...editFields, wechat: e.target.value})}
                          placeholder="微信号" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <input value={editFields.expected_city} onChange={e => setEditFields({...editFields, expected_city: e.target.value})}
                          placeholder="期望城市" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={editFields.portfolio_url} onChange={e => setEditFields({...editFields, portfolio_url: e.target.value})}
                          placeholder="作品集链接" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <input value={editFields.personal_website} onChange={e => setEditFields({...editFields, personal_website: e.target.value})}
                          placeholder="个人网站" className="h-7 px-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={saveEditInfo}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
                          <Check size={13} />保存
                        </button>
                        <button onClick={() => setEditingInfo(false)}
                          className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
                          取消
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ===== DISPLAY MODE ===== */
                    <>
                      {/* Name row */}
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-xl font-bold text-gray-900 tracking-tight">{candidate.name}</h2>
                        {candidate.gender && <span className="text-sm text-gray-500">{candidate.gender}</span>}
                        {candidate.age > 0 && <span className="text-sm text-gray-500">{candidate.age}岁</span>}
                        {candidate.work_years > 0 && (
                          <span className="text-sm text-gray-400 flex items-center gap-0.5"><Clock size={12} />{candidate.work_years}年经验</span>
                        )}
                        {isHr && (
                          <button onClick={startEditInfo} className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors" title="编辑信息">
                            <Edit3 size={13} />
                          </button>
                        )}
                      </div>

                      {/* Job info */}
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                        {candidate.latest_title && (
                          <span className="flex items-center gap-1">
                            <Building2 size={13} className="text-gray-400" />
                            {candidate.latest_company && `${candidate.latest_company} · `}{candidate.latest_title}
                          </span>
                        )}
                        {candidate.education && (
                          <span className="flex items-center gap-1">
                            <GraduationCap size={13} className="text-gray-400" />
                            {candidate.school ? `${candidate.school} · ${candidate.education}` : candidate.education}
                            {candidate.major && ` · ${candidate.major}`}
                          </span>
                        )}
                      </div>

                      {/* Contact info */}
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500">
                        {candidate.phone && <span className="flex items-center gap-1"><Phone size={12} className="text-gray-400" />{candidate.phone}</span>}
                        {candidate.email && <span className="flex items-center gap-1"><Mail size={12} className="text-gray-400" />{candidate.email}</span>}
                        {candidate.wechat && <span className="flex items-center gap-1"><span className="text-gray-400 text-[10px] font-medium">微信</span>{candidate.wechat}</span>}
                        {candidate.expected_city && <span className="flex items-center gap-1"><MapPin size={12} className="text-gray-400" />{candidate.expected_city}</span>}
                        {candidate.portfolio_url && (
                          <a href={candidate.portfolio_url.startsWith('http') ? candidate.portfolio_url : `https://${candidate.portfolio_url}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-0.5 text-primary-600 hover:underline">
                            <Link size={12} />作品集 ↗
                          </a>
                        )}
                        {candidate.personal_website && (
                          <a href={candidate.personal_website.startsWith('http') ? candidate.personal_website : `https://${candidate.personal_website}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-0.5 text-primary-600 hover:underline">
                            <Globe size={12} />个人网站 ↗
                          </a>
                        )}
                      </div>
                    </>
                  )}

                  {/* Tags row */}
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {candidate.tags?.map(tag => (
                      <span key={tag.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                        style={{ backgroundColor: tag.color + '18', color: tag.color }}>
                        {tag.name}
                        {isHr && <button onClick={() => handleRemoveTag(tag.id)} className="hover:opacity-70"><X size={10} /></button>}
                      </span>
                    ))}
                    {isHr && (
                      <div className="relative">
                        <button onClick={() => setShowTagPicker(!showTagPicker)}
                          className="px-2 py-0.5 rounded-full text-[11px] font-medium border border-dashed border-gray-300 text-gray-400 hover:border-primary-400 hover:text-primary-600 transition-colors">
                          + 标签
                        </button>
                        {showTagPicker && (
                          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1.5 z-20 min-w-[180px]">
                            <div className="flex items-center gap-1 mb-1">
                              <input
                                type="text"
                                value={newTagName}
                                onChange={e => setNewTagName(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateAndAddTag(); } }}
                                placeholder="输入新标签名..."
                                className="flex-1 px-2 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:border-primary-400 min-w-0"
                                autoFocus
                              />
                              <button
                                onClick={handleCreateAndAddTag}
                                disabled={!newTagName.trim()}
                                className="shrink-0 px-1.5 py-1 text-[11px] font-medium text-white bg-primary-500 hover:bg-primary-600 rounded disabled:opacity-40 transition-colors">
                                添加
                              </button>
                            </div>
                            {availableTags.length > 0 && (
                              <div className="border-t border-gray-100 pt-1 mt-1 max-h-[150px] overflow-y-auto">
                                {availableTags.map(tag => (
                                  <button key={tag.id}
                                    onClick={() => { handleAddTag(tag.id); setShowTagPicker(false); }}
                                    className="w-full text-left px-2 py-1 text-[11px] rounded hover:bg-gray-50 flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }}></span>
                                    {tag.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                </div>

                {/* Top-right: 分享 / 收藏 / 删除 */}
                <div className="shrink-0 flex items-center gap-1.5">
                  <button onClick={handleShare}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" title="分享（复制链接）">
                    <Share2 size={15} />
                  </button>
                  <button onClick={handleToggleFavorite} disabled={favLoading}
                    className={clsx(
                      'p-1.5 rounded-lg transition-colors disabled:opacity-50',
                      isFavorite ? 'text-amber-500 hover:bg-amber-50' : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50'
                    )}
                    title={isFavorite ? '取消收藏' : '收藏候选人'}>
                    <Star size={15} fill={isFavorite ? 'currentColor' : 'none'} />
                  </button>
                  <button onClick={handleDelete}
                    className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" title="删除">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              </div>
            </div>

            {/* LEFT-BOTTOM: Tab bar + content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Tab bar */}
              <div className="shrink-0 flex items-center gap-0 px-2 md:px-4 bg-white border-b border-gray-200 overflow-x-auto no-scrollbar">
                {([
                  { key: 'resume', label: '简历', count: null },
                  { key: 'screening', label: '筛选', count: screeningRecords.length },
                  { key: 'interview', label: '面试', count: interviewRecords.length },
                  { key: 'offer_decision', label: 'Offer决策', count: salaryMaterials.length + offerApprovals.length },
                  { key: 'application', label: '应聘记录', count: appMaterials.length },
                ] as { key: typeof leftTab; label: string; count: number | null }[]).map(t => (
                  <button key={t.key}
                    onClick={() => setLeftTab(t.key)}
                    className={clsx(
                      'px-3 md:px-4 py-2.5 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                      leftTab === t.key ? 'text-primary-600 border-primary-600' : 'text-gray-500 border-transparent hover:text-gray-700'
                    )}
                  >
                    {t.label}
                    {t.count !== null && t.count > 0 && (
                      <span className="ml-1 md:ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-500">{t.count}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="flex-1 bg-slate-50 overflow-auto">
                {/* ========= RESUME TAB ========= */}
                {leftTab === 'resume' && (
                  <div className="h-full flex flex-col">
                    {resumeUrl ? (
                      <>
                        <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200 shrink-0">
                          <span className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                            <FileText size={13} className="text-gray-400" />候选人原始简历
                          </span>
                          <div className="flex items-center gap-1">
                            <input
                              ref={resumeInputRef}
                              type="file"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={handleResumeFileChange}
                            />
                            {isHr && (
                              <button
                                onClick={() => resumeInputRef.current?.click()}
                                disabled={updatingResume}
                                className="flex items-center gap-1 px-2 py-1 text-[11px] text-gray-500 hover:text-primary-600 hover:bg-gray-50 rounded transition-colors disabled:opacity-50"
                              >
                                <RefreshCw size={12} className={updatingResume ? 'animate-spin' : ''} />
                                {updatingResume ? '更新中...' : '更新简历'}
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                if (aiAnalyzing || !candidate) return;
                                setAiAnalyzing(true); setAiResult(null);
                                try {
                                  const candidatePos = positions.find(p => p.id === candidate.position_id);
                                  let posJd = candidatePos
                                    ? [candidatePos.description, candidatePos.requirements ? `任职要求：\n${candidatePos.requirements}` : ''].filter(Boolean).join('\n\n')
                                    : '';
                                  if (!posJd && candidatePos?.name) posJd = `应聘岗位：${candidatePos.name}`;
                                  const res = await aiAnalyzeResume(candidate.id, true, posJd || undefined);
                                  setAiResult(res.parsed);
                                  toast.success(res.message || 'AI解析完成');
                                  fetchDetail();
                                } catch (err: any) {
                                  toast.error(err?.response?.data?.message || 'AI解析失败');
                                } finally { setAiAnalyzing(false); }
                              }}
                              disabled={aiAnalyzing}
                              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors disabled:opacity-50"
                            >
                              {aiAnalyzing ? (
                                <><span className="animate-spin inline-block w-3 h-3 border border-primary-400 border-t-transparent rounded-full" />分析中...</>
                              ) : (
                                <><Globe size={12} />AI识别简历</>
                              )}
                            </button>
                            {isPdf && !TOUCH_PRIMARY && (
                              <button
                                onClick={() => setHdRender(v => !v)}
                                title={hdRender ? '切回快速预览（浏览器原生）' : '若有生僻字显示不全，点此用高清模式重新渲染'}
                                className={clsx(
                                  'flex items-center gap-1 px-2 py-1 text-[11px] rounded transition-colors',
                                  hdRender
                                    ? 'text-primary-600 bg-primary-50 hover:bg-primary-100'
                                    : 'text-gray-500 hover:text-primary-600 hover:bg-gray-50'
                                )}
                              >
                                <Sparkles size={12} />{hdRender ? '快速预览' : '修复缺字'}
                              </button>
                            )}
                            <a href={resumeUrl} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-1 text-[11px] text-gray-500 hover:text-primary-600 hover:bg-gray-50 rounded transition-colors">
                              <ExternalLink size={12} />新窗口
                            </a>
                            <a href={resumeUrl} download={`${candidate.position_name || '未分配岗位'}_${candidate.name}_${candidate.work_years || 0}年${resumeUrl.substring(resumeUrl.lastIndexOf('.'))}`}
                              className="flex items-center gap-1 px-2 py-1 text-[11px] text-gray-500 hover:text-primary-600 hover:bg-gray-50 rounded transition-colors">
                              <Download size={12} />下载
                            </a>
                          </div>
                        </div>
                        {/* AI Analysis Result Panel */}
                        {aiResult && (
                          <div className="shrink-0 border-b border-gray-200 bg-primary-50 px-4 py-3 max-h-[45%] overflow-y-auto">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-primary-700 flex items-center gap-1.5"><Globe size={13} />AI 简历分析</span>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    const lines: string[] = [];
                                    if (aiResult.jd_match_score) lines.push(`🤖 AI分析 · 岗位匹配度 ${aiResult.jd_match_score}/10`);
                                    if (aiResult.jd_match_summary) lines.push(aiResult.jd_match_summary);
                                    if (aiResult.jd_risks?.length) lines.push(`⚠️ 风险点：${(aiResult.jd_risks as string[]).join('；')}`);
                                    if (aiResult.summary) lines.push(`\n简历摘要：${aiResult.summary}`);
                                    if (aiResult.strengths?.length) lines.push(`✅ 优势：${(aiResult.strengths as string[]).join('、')}`);
                                    setEvalComment(lines.join('\n'));
                                    setRightTab('notes');
                                    toast.success('已写入评论区');
                                  }}
                                  className="text-[11px] text-primary-600 hover:text-primary-800 border border-primary-200 bg-white/70 rounded px-2 py-0.5 transition-colors"
                                >
                                  写入评论
                                </button>
                                <button onClick={() => setAiResult(null)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                              </div>
                            </div>
                            {/* JD Match Score */}
                            {aiResult.jd_match_score > 0 && (
                              <div className="mb-2 p-2 rounded-lg bg-white/80 border border-primary-100 flex items-center gap-3">
                                <span className="text-[11px] font-semibold text-gray-500 shrink-0">岗位匹配</span>
                                <div className="flex items-center gap-0.5 flex-1">
                                  {Array.from({ length: 10 }).map((_, i) => (
                                    <div key={i} className={clsx('h-2.5 flex-1 rounded-sm', i < aiResult.jd_match_score
                                      ? aiResult.jd_match_score >= 8 ? 'bg-primary-600' : aiResult.jd_match_score >= 6 ? 'bg-primary-400' : 'bg-gray-400'
                                      : 'bg-gray-200')} />
                                  ))}
                                </div>
                                <span className={clsx('text-sm font-bold shrink-0',
                                  aiResult.jd_match_score >= 8 ? 'text-primary-600' : aiResult.jd_match_score >= 6 ? 'text-primary-500' : 'text-gray-500'
                                )}>{aiResult.jd_match_score}/10</span>
                              </div>
                            )}
                            {aiResult.jd_match_summary && (
                              <p className="text-[11px] text-primary-800 bg-primary-100/60 rounded px-2 py-1 mb-2">{aiResult.jd_match_summary}</p>
                            )}
                            {aiResult.jd_risks?.length > 0 && (
                              <div className="mb-2">
                                <span className="text-[10px] font-semibold text-gray-700">⚠ 风险点</span>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {(aiResult.jd_risks as string[]).map((r, i) => (
                                    <span key={i} className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-700 rounded border border-gray-200">{r}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {aiResult.summary && <p className="text-xs text-gray-700 mb-2 leading-relaxed">{aiResult.summary}</p>}
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] mb-2">
                              {aiResult.name && <div><span className="text-gray-400">姓名：</span><span className="text-gray-700 font-medium">{aiResult.name}</span></div>}
                              {aiResult.education && <div><span className="text-gray-400">学历：</span><span className="text-gray-700">{aiResult.education}</span></div>}
                              {aiResult.school && <div><span className="text-gray-400">学校：</span><span className="text-gray-700">{aiResult.school}</span></div>}
                              {aiResult.major && <div><span className="text-gray-400">专业：</span><span className="text-gray-700">{aiResult.major}</span></div>}
                              {aiResult.work_years > 0 && <div><span className="text-gray-400">经验：</span><span className="text-gray-700">{aiResult.work_years}年</span></div>}
                              {aiResult.latest_company && <div><span className="text-gray-400">最近公司：</span><span className="text-gray-700">{aiResult.latest_company}</span></div>}
                              {aiResult.latest_title && <div><span className="text-gray-400">最近职位：</span><span className="text-gray-700">{aiResult.latest_title}</span></div>}
                              {aiResult.expected_salary && <div><span className="text-gray-400">期望薪资：</span><span className="text-gray-700">{aiResult.expected_salary}</span></div>}
                              {aiResult.expected_city && <div><span className="text-gray-400">期望城市：</span><span className="text-gray-700">{aiResult.expected_city}</span></div>}
                            </div>
                            {aiResult.strengths?.length > 0 && (
                              <div className="mb-1.5">
                                <span className="text-[10px] font-semibold text-primary-600">优势</span>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {aiResult.strengths.map((s: string, i: number) => (
                                    <span key={i} className="px-1.5 py-0.5 text-[10px] bg-primary-100 text-primary-700 rounded">{s}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {aiResult.concerns?.length > 0 && (
                              <div className="mb-1.5">
                                <span className="text-[10px] font-semibold text-gray-600">关注点</span>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {aiResult.concerns.map((s: string, i: number) => (
                                    <span key={i} className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded">{s}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {aiResult.suitable_roles?.length > 0 && (
                              <div>
                                <span className="text-[10px] font-semibold text-primary-600">适合岗位</span>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {aiResult.suitable_roles.map((s: string, i: number) => (
                                    <span key={i} className="px-1.5 py-0.5 text-[10px] bg-primary-100 text-primary-700 rounded">{s}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex-1 overflow-auto">
                          {isPdf ? (
                            hdRender ? (
                              <PdfViewer
                                url={resumeUrl}
                                className="w-full h-full"
                                downloadName={`${candidate.position_name || '未分配岗位'}_${candidate.name}_${candidate.work_years || 0}年.pdf`}
                              />
                            ) : (
                              <iframe src={`${resumeUrl}#toolbar=0&navpanes=0&view=FitH`} title="Resume" className="w-full h-full border-0" />
                            )
                          ) : isImage ? (
                            <div className="p-4 flex flex-col gap-3 items-center">
                              {resumeImages.map((src, i) => (
                                <img key={i} src={src} alt={`简历截图 ${i + 1}`} className="max-w-full rounded shadow" />
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center justify-center h-full p-8">
                              <div className="text-center text-gray-400">
                                <FileText size={48} strokeWidth={1} className="mx-auto text-gray-300" />
                                <p className="mt-3 text-sm">该格式不支持内嵌预览</p>
                                <a href={resumeUrl} download={`${candidate.position_name || '未分配岗位'}_${candidate.name}_${candidate.work_years || 0}年${resumeUrl.substring(resumeUrl.lastIndexOf('.'))}`} className="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
                                  <Download size={14} />点击下载查看
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : candidate.resume_text ? (
                      <div className="h-full flex flex-col">
                        <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200 shrink-0">
                          <span className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                            <FileText size={13} className="text-gray-400" />在线简历 (插件抓取)
                          </span>
                          <div className="flex items-center gap-2">
                            <input
                              ref={resumeInputRef}
                              type="file"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={handleResumeFileChange}
                            />
                            {isHr && (
                              <button
                                onClick={() => resumeInputRef.current?.click()}
                                disabled={updatingResume}
                                className="flex items-center gap-1 px-2 py-1 text-[11px] text-gray-500 hover:text-primary-600 hover:bg-gray-50 rounded transition-colors disabled:opacity-50"
                              >
                                <RefreshCw size={12} className={updatingResume ? 'animate-spin' : ''} />
                                {updatingResume ? '更新中...' : '更新简历'}
                              </button>
                            )}
                            {(candidate.resume_snapshots?.length || 0) > 0 && (
                              <select
                                className="text-[11px] border border-gray-200 rounded px-1.5 py-0.5 text-gray-500"
                                defaultValue=""
                                onChange={async (e) => {
                                  const snapId = e.target.value;
                                  if (!snapId) return;
                                  try {
                                    const snap = await getResumeSnapshot(candidate.id, snapId);
                                    // Show in a simple alert/modal (or we could use state)
                                    const w = window.open('', '_blank', 'width=600,height=800');
                                    if (w) {
                                      w.document.title = `历史简历 - ${snap.created_at}`;
                                      w.document.body.innerHTML = `<pre style="white-space:pre-wrap;font-family:sans-serif;padding:20px;line-height:1.6">${snap.resume_text}</pre>`;
                                    }
                                  } catch { toast.error('获取历史版本失败'); }
                                  e.target.value = '';
                                }}>
                                <option value="">历史版本 ({candidate.resume_snapshots?.length || 0})</option>
                                {(candidate.resume_snapshots || []).map((s: any) => (
                                  <option key={s.id} value={s.id}>{s.created_at}</option>
                                ))}
                              </select>
                            )}
                            <button onClick={() => { fetchDetail(); toast.success('已刷新'); }}
                              className="flex items-center gap-1 px-2 py-1 text-[11px] text-gray-500 hover:text-primary-600 hover:bg-gray-50 rounded transition-colors">
                              <RefreshCw size={12} />刷新
                            </button>
                          </div>
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">{candidate.resume_text}</pre>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <FileText size={48} strokeWidth={1} className="text-gray-300" />
                        <p className="mt-3 text-sm">暂无简历文件</p>
                        <p className="mt-1 text-xs">使用插件导入时会自动抓取在线简历</p>
                        {isHr && (
                          <>
                            <input
                              ref={resumeInputRef}
                              type="file"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={handleResumeFileChange}
                            />
                            <button
                              onClick={() => resumeInputRef.current?.click()}
                              disabled={updatingResume}
                              className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-50"
                            >
                              <RefreshCw size={13} className={updatingResume ? 'animate-spin' : ''} />
                              {updatingResume ? '更新中...' : '上传简历'}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ========= SCREENING TAB ========= */}
                {leftTab === 'screening' && (
                  <div className="p-5 space-y-4">
                    {/* HR: send to reviewer button */}
                    {isHr && (
                      <div className="flex items-center gap-3">
                        <button onClick={() => { loadFeishuInterviewers(); setShowFeishuPicker(true); }}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors">
                          <Send size={14} />发送给面试官评审
                        </button>
                        <span className="text-[11px] text-gray-400">面试官收到后可直接打开简历并提交筛选结果</span>
                      </div>
                    )}
                    {/* Quick screening buttons (for interviewers) */}
                    {!isHr && (
                      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-gray-700">简历筛选</h4>
                        <input value={scrComment} onChange={e => setScrComment(e.target.value)}
                          placeholder="备注（可选）..."
                          className="w-full h-8 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        <div className="flex items-center gap-3">
                          <button onClick={() => handleAddScreening('passed')}
                            className="flex-1 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm transition-colors">
                            ✓ 简历初筛通过
                          </button>
                          <button onClick={() => handleAddScreening('rejected')}
                            className="flex-1 py-2.5 text-sm font-semibold text-gray-900 bg-white border border-gray-900 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                            ✗ 淘汰
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-400">提交后HR将收到飞书通知</p>
                      </div>
                    )}
                    {/* HR: detailed screening form */}
                    {isHr && (
                      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-gray-700">添加筛选记录</h4>
                        <div className="flex items-center gap-3">
                          <select value={scrResult} onChange={e => setScrResult(e.target.value)}
                            className="h-8 rounded-lg border border-gray-200 px-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                            <option value="passed">通过</option>
                            <option value="rejected">淘汰</option>
                            <option value="pending">待定</option>
                          </select>
                          <input value={scrComment} onChange={e => setScrComment(e.target.value)}
                            placeholder="筛选备注..."
                            className="flex-1 h-8 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                          <button onClick={() => handleAddScreening()}
                            className="h-8 px-4 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
                            添加
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Records list */}
                    {screeningRecords.length === 0 ? (
                      <p className="text-center text-sm text-gray-400 py-8">暂无筛选记录</p>
                    ) : (
                      <div className="space-y-3">
                        {screeningRecords.map((r: any) => (
                          <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500">
                                  {(r.screener_name || '?').charAt(0)}
                                </div>
                                <span className="text-sm font-medium text-gray-700">{r.screener_name || '未知'}</span>
                                <span className={clsx('px-2 py-0.5 rounded-full text-[11px] font-medium',
                                  r.result === 'passed' ? 'bg-primary-100 text-primary-700' :
                                  r.result === 'rejected' ? 'bg-gray-900 text-white' :
                                  'bg-gray-100 text-gray-500'
                                )}>
                                  {r.result === 'passed' ? '通过' : r.result === 'rejected' ? '淘汰' : '待定'}
                                </span>
                              </div>
                              <span className="text-[11px] text-gray-400">{formatBeijingTime(r.created_at, 'datetime')}</span>
                            </div>
                            {r.comment && <p className="text-sm text-gray-600 ml-9">{r.comment}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ========= INTERVIEW TAB ========= */}
                {leftTab === 'interview' && (
                  <div className="p-5 space-y-4">
                    {/* Schedule interview button */}
                    {isHr && !showInterviewForm && (
                      <button onClick={() => setShowInterviewForm(true)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors">
                        <Calendar size={15} />安排面试
                      </button>
                    )}
                    {/* Interview form */}
                    {showInterviewForm && (
                      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-gray-700">安排面试</h4>
                        {/* Type toggle */}
                        <div>
                          <label className="text-[11px] text-gray-500 mb-1.5 block">面试方式</label>
                          <div className="flex gap-2">
                            <button onClick={() => setIvType('video')}
                              className={clsx('flex-1 py-2 rounded-lg text-sm font-medium border transition-colors',
                                ivType === 'video' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                              )}>
                              🖥️ 视频面试
                            </button>
                            <button onClick={() => setIvType('onsite')}
                              className={clsx('flex-1 py-2 rounded-lg text-sm font-medium border transition-colors',
                                ivType === 'onsite' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                              )}>
                              🏢 线下面试
                            </button>
                          </div>
                        </div>
                        {/* Multi-select interviewers */}
                        <div>
                          <label className="text-[11px] text-gray-500 mb-1.5 block">面试官（可多选）</label>
                          <div className="flex flex-wrap gap-1.5 p-2 rounded-lg border border-gray-200 bg-gray-50/50 max-h-32 overflow-y-auto">
                            {selectableUsers.map(u => (
                              <button key={u.id}
                                onClick={() => {
                                  const next = ivInterviewerIds.includes(u.id)
                                    ? ivInterviewerIds.filter(id => id !== u.id)
                                    : [...ivInterviewerIds, u.id];
                                  setIvInterviewerIds(next);
                                  // Fetch freebusy for the last selected interviewer
                                  if (next.length > 0) fetchFreeBusy(next[next.length - 1]);
                                  else { setBusySlots([]); setBusyHint(''); }
                                }}
                                className={clsx(
                                  'px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors',
                                  ivInterviewerIds.includes(u.id)
                                    ? 'bg-primary-50 border-primary-300 text-primary-700'
                                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                                )}
                              >
                                {u.display_name || u.username}{u.feishu_open_id ? ' ✓' : ''}
                              </button>
                            ))}
                          </div>
                          {ivInterviewerIds.length > 0 && (
                            <p className="text-[10px] text-gray-400 mt-1">已选 {ivInterviewerIds.length} 位面试官，每人将分别创建面试记录和飞书日程</p>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[11px] text-gray-500 mb-1 block">轮次</label>
                            <select value={ivRound} onChange={e => setIvRound(e.target.value)}
                              className="w-full h-8 rounded-lg border border-gray-200 px-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                              <option>初试</option>
                              <option>复试</option>
                              <option>终面</option>
                              <option>HR面</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[11px] text-gray-500 mb-1 block">开始时间</label>
                            <input type="datetime-local" value={ivStartTime} onChange={e => { setIvStartTime(e.target.value); handleStartTimeChange(e.target.value, setIvEndTime, ivEndTime); fetchRoomAvailability(e.target.value, calcDuration(e.target.value, ivEndTime)); }}
                              className="w-full h-8 rounded-lg border border-gray-200 px-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                          </div>
                          <div>
                            <label className="text-[11px] text-gray-500 mb-1 block">结束时间</label>
                            {ivStartTime ? (
                              <>
                                <select value={ivEndTime} onChange={e => { setIvEndTime(e.target.value); fetchRoomAvailability(ivStartTime, calcDuration(ivStartTime, e.target.value)); }}
                                  className="w-full h-8 rounded-lg border border-gray-200 px-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                                  {generateEndTimeSlots(ivStartTime).map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                  ))}
                                </select>
                                <input type="datetime-local" value={ivEndTime} onChange={e => { setIvEndTime(e.target.value); fetchRoomAvailability(ivStartTime, calcDuration(ivStartTime, e.target.value)); }}
                                  className="w-full h-6 mt-1 rounded border border-gray-100 px-2 text-[10px] text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500" title="精细调整" />
                              </>
                            ) : (
                              <p className="text-[11px] text-gray-400 h-8 flex items-center">请先选择开始时间</p>
                            )}
                          </div>
                          <div>
                            <label className="text-[11px] text-gray-500 mb-1 block">
                              会议室{meetingRoomSource === 'feishu' ? ' (飞书)' : ''}
                            </label>
                            <select value={ivMeetingRoomId} onChange={e => setIvMeetingRoomId(e.target.value)}
                              className="w-full h-8 rounded-lg border border-gray-200 px-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                              <option value="">选择会议室</option>
                              {meetingRooms.map(r => {
                                const rid = r.room_id || r.id;
                                const busy = ivStartTime && rid ? isRoomBusy(rid, ivStartTime, calcDuration(ivStartTime, ivEndTime)) : false;
                                return (
                                  <option key={rid} value={rid} disabled={busy} style={busy ? { color: '#999' } : {}}>
                                    {busy ? '🔴 ' : '🟢 '}{r.name}{r.floor_name ? ` ${r.floor_name}` : ''}{r.capacity ? ` · ${r.capacity}人` : ''}{busy ? ' (已占用)' : ''}
                                  </option>
                                );
                              })}
                            </select>
                            {meetingRoomHint && <p className="text-[10px] text-gray-500 mt-0.5">{meetingRoomHint}</p>}
                          </div>
                          {ivType === 'onsite' && (
                            <div className="col-span-2">
                              <label className="text-[11px] text-gray-500 mb-1 block">地点备注</label>
                              <input value={ivLocation} onChange={e => setIvLocation(e.target.value)} placeholder="如：前台接待后到3楼"
                                className="w-full h-8 rounded-lg border border-gray-200 px-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                            </div>
                          )}
                        </div>

                        {/* Freebusy display */}
                        {ivInterviewerIds.length > 0 && (
                          <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock size={13} className="text-gray-400" />
                              <span className="text-[11px] font-medium text-gray-500">面试官近7天日程（忙碌时段）</span>
                              {loadingBusy && <span className="text-[10px] text-gray-400">加载中...</span>}
                            </div>
                            {busyHint && <p className="text-[11px] text-gray-500 mb-1">{busyHint}</p>}
                            {busySlots.length === 0 && !loadingBusy && !busyHint && (
                              <p className="text-[11px] text-primary-500">近7天暂无忙碌日程，可自由安排 ✓</p>
                            )}
                            {busySlots.length > 0 && (
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {busySlots.map((slot, i) => {
                                  const s = new Date(slot.start);
                                  const e = new Date(slot.end);
                                  const fmt = (d: Date) => `${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
                                  return (
                                    <div key={i} className="flex items-center gap-2 text-[11px]">
                                      <span className="w-2 h-2 rounded-full bg-gray-400 shrink-0" />
                                      <span className="text-gray-600">{fmt(s)} ~ {fmt(e)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-2 pt-1">
                          <button onClick={handleScheduleInterview}
                            className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
                            确认安排
                          </button>
                          <button onClick={() => { setShowInterviewForm(false); setIvInterviewerIds([]); setBusySlots([]); setBusyHint(''); }}
                            className="px-4 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
                            取消
                          </button>
                          <span className="text-[11px] text-gray-400 ml-auto">
                            将自动创建飞书日程并通知面试官
                          </span>
                        </div>
                      </div>
                    )}
                    {/* Records list */}
                    {interviewRecords.length === 0 && !showInterviewForm ? (
                      <p className="text-center text-sm text-gray-400 py-8">暂无面试记录</p>
                    ) : (
                      <div className="space-y-3">
                        {interviewRecords.map((r: any) => (
                          <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-4">
                            {/* Header row */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                {/* Interviewer avatars */}
                                <div className="flex items-center -space-x-1.5">
                                  {(r.interviewers && r.interviewers.length > 0 ? r.interviewers : [{ id: r.interviewer_id, display_name: r.interviewer_name || '?' }]).map((iv: any, idx: number) => (
                                    <div key={iv.id || idx} className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-[11px] font-bold text-primary-600 border-2 border-white" title={iv.display_name}>
                                      {(iv.display_name || '?').charAt(0)}
                                    </div>
                                  ))}
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                  {(r.interviewers && r.interviewers.length > 0 ? r.interviewers.map((iv: any) => iv.display_name) : [r.interviewer_name || '未知']).join('、')}
                                </span>
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary-50 text-primary-600">{r.round || '面试'}</span>
                                <span className={clsx('px-2 py-0.5 rounded-full text-[11px] font-medium',
                                  r.interview_type === 'onsite' ? 'bg-primary-50 text-primary-600' : 'bg-primary-50 text-primary-600'
                                )}>
                                  {r.interview_type === 'onsite' ? '🏢 线下' : '🖥️ 视频'}
                                </span>
                                <span className={clsx('px-2 py-0.5 rounded-full text-[11px] font-medium',
                                  r.status === 'scheduled' ? 'bg-gray-100 text-gray-700' :
                                  r.status === 'completed' ? 'bg-primary-100 text-primary-700' :
                                  'bg-gray-100 text-gray-500'
                                )}>
                                  {r.status === 'scheduled' ? '待进行' : r.status === 'completed' ? '已完成' : '已取消'}
                                </span>
                                {r.feishu_event_id && <span className="text-[10px] text-primary-400">📅</span>}
                              </div>
                              {isHr && r.status === 'scheduled' && editingInterviewId !== r.id && (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      setEditingInterviewId(r.id);
                                      // Convert to datetime-local format (YYYY-MM-DDTHH:MM)
                                      const toLocal = (dt: string) => {
                                        if (!dt) return '';
                                        const d = new Date(dt.includes('+') || dt.includes('Z') ? dt : dt + '+08:00');
                                        const pad = (n: number) => String(n).padStart(2, '0');
                                        const bj = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Shanghai' }));
                                        return `${bj.getFullYear()}-${pad(bj.getMonth() + 1)}-${pad(bj.getDate())}T${pad(bj.getHours())}:${pad(bj.getMinutes())}`;
                                      };
                                      setEditIvStartTime(toLocal(r.scheduled_time));
                                      setEditIvEndTime(r.end_time ? toLocal(r.end_time) : toLocal(new Date(new Date(r.scheduled_time.includes('+') || r.scheduled_time.includes('Z') ? r.scheduled_time : r.scheduled_time + '+08:00').getTime() + (r.duration_minutes || 60) * 60000).toISOString()));
                                      setEditIvLocation(r.location || ''); setEditIvType(r.interview_type || 'video');
                                      setEditIvRound(r.round || ''); setEditIvInterviewerId(r.interviewer_id || '');
                                      setEditIvMeetingRoomId(r.meeting_room_id || '');
                                    }}
                                    className="text-xs text-primary-500 hover:text-primary-700 font-medium flex items-center gap-1"
                                  >
                                    <Edit3 size={12} />调整
                                  </button>
                                  <button
                                    onClick={() => handleCancelInterview(r.id)}
                                    className="text-xs text-gray-400 hover:text-gray-600 font-medium flex items-center gap-1"
                                  >
                                    <X size={12} />取消面试
                                  </button>
                                </div>
                              )}
                            </div>
                            {/* Interviewer management: add/remove */}
                            {isHr && r.status === 'scheduled' && editingInterviewId !== r.id && (
                              <div className="ml-9 mb-2 flex items-center gap-1.5 flex-wrap">
                                {(r.interviewers || []).map((iv: any) => (
                                  <span key={iv.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary-50 text-primary-600 border border-primary-100">
                                    {iv.display_name}
                                    {(r.interviewers || []).length > 1 && (
                                      <button
                                        onClick={async () => {
                                          if (!window.confirm(`确定移除面试官 ${iv.display_name}？将同步更新飞书日程。`)) return;
                                          try {
                                            await removeInterviewInterviewer(r.id, iv.id);
                                            toast.success(`已移除 ${iv.display_name}`);
                                            setInterviewRecords(await getInterviewRecords(candidateId));
                                          } catch (err: any) { toast.error(err.response?.data?.message || '移除失败'); }
                                        }}
                                        className="text-primary-400 hover:text-gray-500 transition-colors" title="移除"
                                      ><X size={10} /></button>
                                    )}
                                  </span>
                                ))}
                                <select
                                  value=""
                                  onChange={async (e) => {
                                    const userId = e.target.value;
                                    if (!userId) return;
                                    try {
                                      await addInterviewInterviewer(r.id, userId);
                                      const u = allUsers.find(u => u.id === userId);
                                      toast.success(`已添加 ${u?.display_name || '面试官'}，飞书日程已同步`);
                                      setInterviewRecords(await getInterviewRecords(candidateId));
                                    } catch (err: any) { toast.error(err.response?.data?.message || '添加失败'); }
                                  }}
                                  className="h-5 rounded border border-dashed border-primary-200 px-1 text-[10px] text-primary-500 bg-transparent focus:outline-none cursor-pointer hover:border-primary-400"
                                >
                                  <option value="">+ 面试官</option>
                                  {selectableUsers.filter(u => !(r.interviewers || []).some((iv: any) => iv.id === u.id)).map(u => (
                                    <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                            {/* Time & Location - BIG */}
                            <div className="ml-9 mb-2">
                              <div className="flex items-center gap-3 mb-1">
                                <span className="text-base font-bold text-gray-800">
                                  {r.scheduled_time ? (() => {
                                    const fmt = (dt: string) => new Date(dt.includes('+') || dt.includes('Z') ? dt : dt + '+08:00').toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short' });
                                    const fmtTime = (dt: string) => new Date(dt.includes('+') || dt.includes('Z') ? dt : dt + '+08:00').toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit' });
                                    const startStr = fmt(r.scheduled_time);
                                    if (r.end_time) {
                                      return `${startStr} - ${fmtTime(r.end_time)}`;
                                    }
                                    return startStr;
                                  })() : '时间未定'}
                                </span>
                                <span className="text-sm text-gray-500">{r.duration_minutes}分钟</span>
                              </div>
                              {r.location && (
                                <div className="flex items-center gap-1.5">
                                  <MapPin size={13} className="text-gray-400 shrink-0" />
                                  <span className="text-sm font-medium text-gray-600">{r.location}</span>
                                  {r.meeting_room_name && r.meeting_room_name !== r.location && (
                                    <span className="text-xs text-gray-400">({r.meeting_room_name})</span>
                                  )}
                                </div>
                              )}
                            </div>
                            {/* Meta + Feedback */}
                            <div className="ml-9 text-xs text-gray-400 space-y-1">
                              {r.creator_name && <p>安排人: {r.creator_name}</p>}
                              {(() => {
                                const interviewEvaluations = (candidate.evaluations || []).filter((ev: any) => ev.interview_record_id === r.id);
                                const myInterviewEvaluation = interviewEvaluations.find((ev: any) => ev.user_id === currentUser.id);
                                const canWriteInterviewEvaluation = isHr || (r.interviewers || []).some((iv: any) => iv.id === currentUser.id);
                                const legacyFeedbackOnly = r.feedback && interviewEvaluations.length === 0;
                                // Check if there are interviewers who haven't submitted evaluations
                                // 代交的面评算在被代表的面试官名下，否则代交后仍会催 TA
                                const evaluatedUserIds = new Set(interviewEvaluations.map((ev: any) => ev.on_behalf_of || ev.user_id));
                                const pendingEvalInterviewers = (r.interviewers || []).filter((iv: any) => !evaluatedUserIds.has(iv.id));
                                const canNudge = isHr && r.status !== 'cancelled' && pendingEvalInterviewers.length > 0;

                                return (
                                  <>
                                    {legacyFeedbackOnly && (
                                      <div className="mt-2 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-[11px] font-medium text-gray-600">面试评价</span>
                                          {r.rating > 0 && <span className="text-[11px] font-semibold text-primary-600">{Number(r.rating).toFixed(1)} 分</span>}
                                        </div>
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.feedback}</p>
                                      </div>
                                    )}
                                    {interviewEvaluations.length > 0 && (
                                      <div className="mt-2 space-y-2">
                                        {interviewEvaluations.map((ev: any) => (
                                          <div key={ev.id} className="p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                              <div className="flex items-center gap-2">
                                                <span className="text-[11px] font-medium text-gray-600">{ev.on_behalf_of_name || ev.author_name || '未知面试官'}</span>
                                                {ev.on_behalf_of_name && (
                                                  <span className="text-[10px] text-gray-400">由 {ev.author_name} 代交</span>
                                                )}
                                                {ev.rating > 0 && <span className="text-[11px] font-semibold text-primary-600">{Number(ev.rating).toFixed(1)} 分</span>}
                                                {ev.user_id === currentUser.id && <span className="text-[10px] text-gray-400">我的评价</span>}
                                                {ev.updated_at && ev.updated_at !== ev.created_at && (
                                                  <span className="text-[10px] text-gray-500">已修改</span>
                                                )}
                                              </div>
                                              {(ev.user_id === currentUser.id || isHr) && (
                                                <button
                                                  onClick={async () => {
                                                    if (viewingHistoryEvalId === ev.id) {
                                                      setViewingHistoryEvalId(null);
                                                      setEvaluationHistory([]);
                                                    } else {
                                                      try {
                                                        const history = await getEvaluationHistory(ev.id);
                                                        setEvaluationHistory(history);
                                                        setViewingHistoryEvalId(ev.id);
                                                      } catch (err: any) {
                                                        toast.error(err.response?.data?.message || '获取历史记录失败');
                                                      }
                                                    }
                                                  }}
                                                  className="text-gray-400 hover:text-primary-600 transition-colors"
                                                  title="查看修改历史"
                                                >
                                                  <History size={14} />
                                                </button>
                                              )}
                                            </div>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{ev.comment}</p>
                                            {viewingHistoryEvalId === ev.id && evaluationHistory.length > 0 && (
                                              <div className="mt-2 pt-2 border-t border-gray-200">
                                                <p className="text-[10px] font-medium text-gray-500 mb-1.5">修改历史</p>
                                                <div className="space-y-1.5">
                                                  {evaluationHistory.map((h: any) => (
                                                    <div key={h.id} className="text-[11px] bg-white p-2 rounded border border-gray-100">
                                                      <div className="flex items-center gap-2 mb-1 text-gray-500">
                                                        <span>{formatBeijingTime(h.modified_at, 'datetime')}</span>
                                                        <span>·</span>
                                                        <span>{h.modifier_name || '未知'}</span>
                                                        {h.rating > 0 && <span className="text-primary-600 font-semibold">{Number(h.rating).toFixed(1)} 分</span>}
                                                      </div>
                                                      <p className="text-gray-700 whitespace-pre-wrap">{h.comment}</p>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                            {viewingHistoryEvalId === ev.id && evaluationHistory.length === 0 && (
                                              <div className="mt-2 pt-2 border-t border-gray-200">
                                                <p className="text-[10px] text-gray-400">暂无修改历史</p>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {canWriteInterviewEvaluation && r.status !== 'cancelled' && feedbackInterviewId !== r.id && (
                                      <button
                                        onClick={() => {
                                          setFeedbackInterviewId(r.id);
                                          const draftKey = `feedback_draft_${r.id}_${currentUser.id}`;
                                          const saved = localStorage.getItem(draftKey);
                                          if (saved) {
                                            try {
                                              const draft = JSON.parse(saved);
                                              setFeedbackText(draft.text || myInterviewEvaluation?.comment || '');
                                              setFeedbackRating(draft.rating ?? myInterviewEvaluation?.rating ?? 0);
                                              if (draft.text) toast.success('已恢复上次未提交的评价草稿');
                                            } catch {
                                              setFeedbackText(myInterviewEvaluation?.comment || '');
                                              setFeedbackRating(myInterviewEvaluation?.rating || 0);
                                            }
                                          } else {
                                            setFeedbackText(myInterviewEvaluation?.comment || '');
                                            setFeedbackRating(myInterviewEvaluation?.rating || 0);
                                          }
                                        }}
                                        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                                      >
                                        <Edit3 size={12} />{myInterviewEvaluation ? '编辑我的评价' : '写评价'}
                                      </button>
                                    )}
                                    {canNudge && feedbackInterviewId !== r.id && (
                                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                                        {pendingEvalInterviewers.map((iv: any) => (
                                          <button
                                            key={iv.id}
                                            onClick={async () => {
                                              try {
                                                const result = await nudgeInterviewEvaluation(r.id, [iv.id]);
                                                toast.success(result.message || `已提醒 ${iv.display_name}`);
                                              } catch (err: any) {
                                                toast.error(err?.response?.data?.message || '催评价失败');
                                              }
                                            }}
                                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                                            title={`催促 ${iv.display_name} 提交评价`}
                                          >
                                            <Send size={11} />催 {iv.display_name}
                                          </button>
                                        ))}
                                        {pendingEvalInterviewers.length > 1 && (
                                          <button
                                            onClick={async () => {
                                              try {
                                                const result = await nudgeInterviewEvaluation(r.id);
                                                toast.success(result.message || '已发送催评价通知');
                                              } catch (err: any) {
                                                toast.error(err?.response?.data?.message || '催评价失败');
                                              }
                                            }}
                                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-gray-500 border border-gray-500 rounded-lg hover:bg-gray-600 transition-colors"
                                          >
                                            <Send size={11} />催全部
                                          </button>
                                        )}
                                      </div>
                                    )}
                                    {canWriteInterviewEvaluation && feedbackInterviewId === r.id && (
                                      <div className="mt-2 p-3 rounded-lg border border-primary-200 bg-primary-50/30 space-y-2.5">
                                        <p className="text-[11px] font-medium text-gray-600">{myInterviewEvaluation ? '编辑我的面试评价' : '提交面试评价'}</p>
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <label className="text-[10px] text-gray-500">评分</label>
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-sm font-bold text-primary-600">{feedbackRating > 0 ? feedbackRating.toFixed(1) : '-'}</span>
                                              {feedbackRating > 0 && (() => {
                                                const level = getRatingLevel(feedbackRating);
                                                return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${level.bg} ${level.color}`}>{level.label}</span>;
                                              })()}
                                            </div>
                                          </div>
                                          <input type="range" min="0" max="100" step="1" value={feedbackRating * 10}
                                            onChange={e => {
                                              const val = Math.round(parseInt(e.target.value)) / 10;
                                              setFeedbackRating(val);
                                              localStorage.setItem(`feedback_draft_${r.id}_${currentUser.id}`, JSON.stringify({ text: feedbackText, rating: val, savedAt: Date.now() }));
                                            }}
                                            className="w-full h-2 rounded-full appearance-none cursor-pointer"
                                            style={{ background: feedbackRating > 0
                                              ? `linear-gradient(to right, ${getRatingTrackColor(feedbackRating)} 0%, ${getRatingTrackColor(feedbackRating)} ${feedbackRating * 10}%, #e5e7eb ${feedbackRating * 10}%, #e5e7eb 100%)`
                                              : '#e5e7eb' }}
                                          />
                                        </div>
                                        <textarea
                                          value={feedbackText}
                                          onChange={e => {
                                            setFeedbackText(e.target.value);
                                            localStorage.setItem(`feedback_draft_${r.id}_${currentUser.id}`, JSON.stringify({ text: e.target.value, rating: feedbackRating, savedAt: Date.now() }));
                                          }}
                                          placeholder="请输入面试反馈和评价...（草稿自动保存）"
                                          rows={3}
                                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none bg-white"
                                        />
                                        <div className="flex items-center gap-2">
                                          <button
                                            disabled={!feedbackText.trim() || feedbackRating === 0}
                                            onClick={async () => {
                                              try {
                                                if (myInterviewEvaluation?.id) {
                                                  await updateEvaluation(myInterviewEvaluation.id, { comment: feedbackText.trim(), rating: feedbackRating });
                                                } else {
                                                  await createEvaluation({
                                                    candidate_id: candidateId,
                                                    interview_record_id: r.id,
                                                    stage: r.round || candidate.stage,
                                                    rating: feedbackRating,
                                                    comment: feedbackText.trim(),
                                                    result: 'pending',
                                                    is_private: false,
                                                  });
                                                }
                                                localStorage.removeItem(`feedback_draft_${r.id}_${currentUser.id}`);
                                                toast.success('面试评价已提交');
                                                setFeedbackInterviewId(null);
                                                fetchDetail();
                                              } catch (err: any) {
                                                toast.error(err?.response?.data?.message || '提交失败');
                                              }
                                            }}
                                            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-40"
                                          >
                                            提交评价
                                          </button>
                                          <button onClick={() => setFeedbackInterviewId(null)}
                                            className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
                                            取消
                                          </button>
                                          <span className="text-[10px] text-gray-400 ml-auto">草稿自动保存 · 提交后将通知HR</span>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                            {/* Full edit form */}
                            {editingInterviewId === r.id && (
                              <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                                <p className="text-xs font-medium text-gray-500">调整面试{r.feishu_event_id ? ' · 飞书日程将同步更新' : ''}</p>
                                {/* Type toggle */}
                                <div className="flex gap-2">
                                  <button onClick={() => setEditIvType('video')}
                                    className={clsx('flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                                      editIvType === 'video' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-500'
                                    )}>🖥️ 视频</button>
                                  <button onClick={() => setEditIvType('onsite')}
                                    className={clsx('flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                                      editIvType === 'onsite' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-500'
                                    )}>🏢 线下</button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="text-[10px] text-gray-400 mb-0.5 block">轮次</label>
                                    <select value={editIvRound} onChange={e => setEditIvRound(e.target.value)}
                                      className="w-full h-7 rounded border border-gray-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500">
                                      <option>初试</option><option>复试</option><option>终面</option><option>HR面</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-[10px] text-gray-400 mb-0.5 block">开始时间</label>
                                    <input type="datetime-local" value={editIvStartTime} onChange={e => { setEditIvStartTime(e.target.value); handleStartTimeChange(e.target.value, setEditIvEndTime, editIvEndTime); fetchRoomAvailability(e.target.value, calcDuration(e.target.value, editIvEndTime)); }}
                                      className="w-full h-7 rounded border border-gray-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500" />
                                  </div>
                                  <div>
                                    <label className="text-[10px] text-gray-400 mb-0.5 block">结束时间</label>
                                    {editIvStartTime ? (
                                      <>
                                        <select value={editIvEndTime} onChange={e => { setEditIvEndTime(e.target.value); fetchRoomAvailability(editIvStartTime, calcDuration(editIvStartTime, e.target.value)); }}
                                          className="w-full h-7 rounded border border-gray-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500">
                                          {generateEndTimeSlots(editIvStartTime).map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                          ))}
                                        </select>
                                        <input type="datetime-local" value={editIvEndTime} onChange={e => { setEditIvEndTime(e.target.value); fetchRoomAvailability(editIvStartTime, calcDuration(editIvStartTime, e.target.value)); }}
                                          className="w-full h-5 mt-0.5 rounded border border-gray-100 px-1.5 text-[10px] text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500" title="精细调整" />
                                      </>
                                    ) : (
                                      <p className="text-[10px] text-gray-400 h-7 flex items-center">请先选择开始时间</p>
                                    )}
                                  </div>
                                  <div>
                                    <label className="text-[10px] text-gray-400 mb-0.5 block">会议室{meetingRoomSource === 'feishu' ? ' (飞书)' : ''}</label>
                                    <select value={editIvMeetingRoomId} onChange={e => setEditIvMeetingRoomId(e.target.value)}
                                      className="w-full h-7 rounded border border-gray-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500">
                                      <option value="">选择会议室</option>
                                      {meetingRooms.map(rm => {
                                        const rid = rm.room_id || rm.id;
                                        const busy = editIvStartTime && rid ? isRoomBusy(rid, editIvStartTime, calcDuration(editIvStartTime, editIvEndTime)) : false;
                                        return (
                                          <option key={rid} value={rid} disabled={busy} style={busy ? { color: '#999' } : {}}>
                                            {busy ? '🔴 ' : '🟢 '}{rm.name}{rm.floor_name ? ` ${rm.floor_name}` : ''}{busy ? ' (已占用)' : ''}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>
                                  {editIvType === 'onsite' && (
                                    <div>
                                      <label className="text-[10px] text-gray-400 mb-0.5 block">地点备注</label>
                                      <input value={editIvLocation} onChange={e => setEditIvLocation(e.target.value)}
                                        className="w-full h-7 rounded border border-gray-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <button onClick={() => handleReschedule(r.id)}
                                    className="px-3 py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors">
                                    确认调整
                                  </button>
                                  <button onClick={() => setEditingInterviewId(null)}
                                    className="px-3 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded transition-colors">
                                    取消
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ========= OFFER DECISION TAB (merged salary + offer) ========= */}
                {leftTab === 'offer_decision' && (
                  <div className="p-5 space-y-5">
                    {/* === Section: Offer 与编制占用（新链路 /api/offers；薪酬材料已并入面板）=== */}
                    <OfferDecisionPanel
                      candidate={{ id: candidate.id, name: candidate.name, position_id: candidate.position_id, stage: candidate.stage }}
                      currentUser={currentUser}
                      isHr={isHr}
                      users={selectableUsers}
                      onChanged={() => { fetchDetail(); onUpdate(); }}
                    />

                    {/* === Section: 历史 Offer 审批记录（旧流程，只读）=== */}
                    {offerApprovals.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-500 flex items-center gap-1.5">
                          <span className="w-1.5 h-4 rounded-full bg-gray-300 inline-block" />
                          历史 Offer 审批（旧流程 · 只读）
                          <span className="text-xs text-gray-400 font-normal">({offerApprovals.length})</span>
                        </h3>
                      </div>

                      <div className="space-y-2.5">
                          {offerApprovals.map((r: any) => {
                            const stepLabel = r.step === 'pdf' ? 'PDF附件审批' : '方案审批';
                            const stepColor = r.step === 'pdf' ? 'bg-primary-50 text-primary-600' : 'bg-primary-50 text-primary-600';
                            return (
                              <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-3.5">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className={clsx('px-2 py-0.5 rounded-full text-[11px] font-medium', stepColor)}>{stepLabel}</span>
                                    <span className="text-xs font-medium text-gray-700">{r.submitter_name || '未知'}</span>
                                    <span className={clsx('px-2 py-0.5 rounded-full text-[11px] font-medium',
                                      r.status === 'pending' ? 'bg-gray-100 text-gray-700' :
                                      r.status === 'approved' ? 'bg-primary-100 text-primary-700' :
                                      r.status === 'withdrawn' ? 'bg-gray-100 text-gray-500' :
                                      'bg-gray-100 text-gray-600'
                                    )}>
                                      {r.status === 'pending' ? '待审批' : r.status === 'approved' ? '已通过' : r.status === 'withdrawn' ? '已撤销' : '已拒绝'}
                                    </span>
                                  </div>
                                  <span className="text-[11px] text-gray-400">{formatBeijingTime(r.created_at, 'datetime')}</span>
                                </div>
                                {r.reason && <p className="text-xs text-gray-600 mb-2">{r.reason}</p>}
                                {r.offer_image && (
                                  /\.pdf$/i.test(r.offer_image) ? (
                                    <a href={r.offer_image} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline mb-2">
                                      <FileText size={12} />查看方案PDF
                                    </a>
                                  ) : (
                                    <img src={r.offer_image} alt="Offer方案" className="max-w-xs rounded-lg border border-gray-200 mb-2 cursor-pointer hover:opacity-90"
                                      onClick={() => window.open(r.offer_image, '_blank')} />
                                  )
                                )}
                                {r.offer_pdf && (
                                  <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-primary-50 border border-primary-100">
                                    <span className="text-xs font-bold text-gray-500">PDF</span>
                                    <a href={r.offer_pdf} target="_blank" rel="noreferrer" className="text-xs text-primary-600 hover:underline">查看Offer PDF</a>
                                  </div>
                                )}
                                {r.status !== 'pending' && r.status !== 'withdrawn' && r.approver_name && (
                                  <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                                    审批人: {r.approver_name} · {formatBeijingTime(r.approved_at, 'datetime')}
                                    {r.approver_comment && <span className="ml-2">意见: {r.approver_comment}</span>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                    )}
                  </div>
                )}

                {/* ========= APPLICATION MATERIALS TAB ========= */}
                {leftTab === 'application' && (
                  <div className="p-5 space-y-4">
                    {/* Candidate application info summary */}
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700">投递信息</h4>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-gray-400">投递渠道：</span>
                          <span className="text-gray-700 font-medium">{candidate.source === 'referral' ? '内推' : candidate.source || '未知'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">投递时间：</span>
                          <span className="text-gray-700 font-medium">{formatBeijingTime(candidate.created_at, 'datetime')}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">目标岗位：</span>
                          <span className="text-gray-700 font-medium">{positions.find(p => p.id === candidate.position_id)?.name || '人才池'}</span>
                        </div>
                        {candidate.referrer_name && (
                          <div>
                            <span className="text-gray-400">内推码：</span>
                            <span className="text-primary-600 font-medium">{candidate.referrer_name}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Upload button */}
                    {isHr && (
                      <button
                        onClick={() => { setShowAppMaterialForm(!showAppMaterialForm); setAppMatType('cover_letter'); setAppMatTitle(''); setAppMatContent(''); setAppMatFile(null); setAppMatRecName(''); setAppMatRecRelation(''); setAppMatRecContact(''); }}
                        className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        {showAppMaterialForm ? '取消' : '+ 添加应聘材料'}
                      </button>
                    )}

                    {/* Upload form */}
                    {showAppMaterialForm && (
                      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                        <div>
                          <label className="text-[11px] text-gray-500 mb-1 block">材料类型</label>
                          <select value={appMatType} onChange={e => setAppMatType(e.target.value as any)}
                            className="w-full h-8 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                            <option value="cover_letter">自荐信 / 求职信</option>
                            <option value="recommendation">推荐信</option>
                            <option value="attachment">其他附件</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[11px] text-gray-500 mb-1 block">标题</label>
                          <input value={appMatTitle} onChange={e => setAppMatTitle(e.target.value)}
                            placeholder="如：候选人自荐信、张三推荐信..."
                            className="w-full h-8 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                        </div>
                        {(appMatType === 'cover_letter') && (
                          <div>
                            <label className="text-[11px] text-gray-500 mb-1 block">文字内容</label>
                            <textarea value={appMatContent} onChange={e => setAppMatContent(e.target.value)} rows={4}
                              placeholder="粘贴自荐信/求职信内容..."
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
                          </div>
                        )}
                        {appMatType === 'recommendation' && (
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="text-[11px] text-gray-500 mb-1 block">推荐人</label>
                              <input value={appMatRecName} onChange={e => setAppMatRecName(e.target.value)} placeholder="姓名"
                                className="w-full h-8 rounded-lg border border-gray-200 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500 mb-1 block">关系</label>
                              <input value={appMatRecRelation} onChange={e => setAppMatRecRelation(e.target.value)} placeholder="如：前主管"
                                className="w-full h-8 rounded-lg border border-gray-200 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500 mb-1 block">联系方式</label>
                              <input value={appMatRecContact} onChange={e => setAppMatRecContact(e.target.value)} placeholder="手机/邮箱"
                                className="w-full h-8 rounded-lg border border-gray-200 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="text-[11px] text-gray-500 mb-1 block">上传文件（可选）</label>
                          <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={e => setAppMatFile(e.target.files?.[0] || null)}
                            className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200" />
                        </div>
                        <button
                          onClick={async () => {
                            if (!appMatTitle.trim() && !appMatContent.trim() && !appMatFile) {
                              toast.error('请填写内容或上传文件'); return;
                            }
                            try {
                              const fd = new FormData();
                              fd.append('candidate_id', candidateId);
                              fd.append('type', appMatType);
                              fd.append('title', appMatTitle.trim());
                              fd.append('content', appMatContent.trim());
                              if (appMatFile) fd.append('file', appMatFile);
                              if (appMatType === 'recommendation') {
                                fd.append('recommender_name', appMatRecName);
                                fd.append('recommender_relation', appMatRecRelation);
                                fd.append('recommender_contact', appMatRecContact);
                              }
                              await createApplicationMaterial(fd);
                              toast.success('材料已上传');
                              setShowAppMaterialForm(false);
                              setAppMaterials(await getApplicationMaterials(candidateId));
                            } catch { toast.error('上传失败'); }
                          }}
                          className="w-full h-8 flex items-center justify-center text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                        >
                          提交
                        </button>
                      </div>
                    )}

                    {/* Materials list */}
                    {appMaterials.length === 0 && !showAppMaterialForm && (
                      <div className="text-center py-10 text-gray-400 text-sm">暂无应聘材料</div>
                    )}
                    {appMaterials.map(m => {
                      const typeLabels: Record<string, string> = { cover_letter: '自荐信', recommendation: '推荐信', attachment: '附件', email_body: '邮件正文' };
                      const typeColors: Record<string, string> = { cover_letter: 'bg-primary-50 text-primary-600', recommendation: 'bg-primary-50 text-primary-600', attachment: 'bg-gray-100 text-gray-600', email_body: 'bg-primary-50 text-primary-600' };
                      return (
                        <div key={m.id} className="bg-white rounded-xl border border-gray-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={clsx('text-[10px] font-medium px-2 py-0.5 rounded-full', typeColors[m.type] || 'bg-gray-100 text-gray-500')}>
                                {typeLabels[m.type] || m.type}
                              </span>
                              <span className="text-sm font-medium text-gray-700">{m.title || '未命名'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px] text-gray-400">
                              <span>{m.uploader_name || ''}</span>
                              <span>{formatBeijingTime(m.created_at, 'datetime')}</span>
                              {isHr && (
                                <button onClick={async () => {
                                  if (!window.confirm('确认删除？')) return;
                                  try { await deleteApplicationMaterial(m.id); toast.success('已删除'); setAppMaterials(await getApplicationMaterials(candidateId)); } catch { toast.error('删除失败'); }
                                }} className="text-gray-300 hover:text-gray-500 transition-colors"><Trash2 size={12} /></button>
                              )}
                            </div>
                          </div>
                          {m.content && (
                            <div className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 mb-2">{m.content}</div>
                          )}
                          {m.type === 'recommendation' && (m.recommender_name || m.recommender_relation || m.recommender_contact) && (
                            <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                              {m.recommender_name && <span>推荐人：{m.recommender_name}</span>}
                              {m.recommender_relation && <span>关系：{m.recommender_relation}</span>}
                              {m.recommender_contact && <span>联系方式：{m.recommender_contact}</span>}
                            </div>
                          )}
                          {m.file_path && (
                            <a href={m.file_path} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700">
                              <FileText size={12} /> 查看附件
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ====== RIGHT COLUMN ====== */}
          <div className={clsx(
            'w-full md:w-[25%] flex flex-col overflow-hidden bg-white border-t md:border-t-0 border-gray-200',
            mobileView !== 'right' && 'hidden md:flex'
          )}>

            {/* RIGHT-TOP: 4 Action Buttons (2x2) + Rating */}
            <div className="shrink-0 px-3 pt-12 pb-2.5 md:px-5 md:pt-12 md:pb-3 border-b border-gray-200 space-y-2">
              {/* Pass/Reject buttons */}
              <div className="space-y-2">
                {/* Pass / Reject buttons */}
                {(() => {
                  // 使用主流程阶段计算下一个阶段（跳过淘汰阶段）
                  const currentMainFlowIdx = MAIN_FLOW_STAGES.indexOf(candidate.stage as CandidateStage);
                  const nextMainStage = currentMainFlowIdx >= 0 ? MAIN_FLOW_STAGES[currentMainFlowIdx + 1] : null;
                  const isTerminal = candidate.stage === 'onboarded';
                  if (isTerminal) return null;

                  // Map rejected stages back to their corresponding active stage (for "revive" / pass action)
                  const rejectedToActive: Partial<Record<CandidateStage, CandidateStage>> = {
                    screening_rejected: 'screening_passed',
                    first_interview_rejected: 'first_interview',
                    second_interview_rejected: 'second_interview',
                    final_interview_rejected: 'final_interview',
                    offer_rejected: 'offer',
                  };
                  const isRejected = candidate.stage in rejectedToActive;
                  const reviveStage = rejectedToActive[candidate.stage as CandidateStage] ?? null;

                  // Map active stages to their rejected counterpart (rejected stages map to themselves)
                  const activeToRejected: Partial<Record<CandidateStage, CandidateStage>> = {
                    new: 'screening_rejected',
                    screening: 'screening_rejected',
                    screening_passed: 'screening_rejected',
                    first_interview: 'first_interview_rejected',
                    second_interview: 'second_interview_rejected',
                    final_interview: 'final_interview_rejected',
                    offer: 'offer_rejected',
                    // Allow re-rejection on already-rejected stages (records a new log entry)
                    screening_rejected: 'screening_rejected',
                    first_interview_rejected: 'first_interview_rejected',
                    second_interview_rejected: 'second_interview_rejected',
                    final_interview_rejected: 'final_interview_rejected',
                    offer_rejected: 'offer_rejected',
                  };
                  const rejectStage = activeToRejected[candidate.stage as CandidateStage] ?? null;

                  if (isHr) {
                    return (
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            const target = isRejected ? reviveStage : nextMainStage;
                            if (!target) return;
                            setStampAnimation('pass');
                            await handleStageChange(target);
                            setTimeout(() => {
                              setStampAnimation(null);
                              if (nextId && onNavigate) onNavigate(nextId);
                            }, 800);
                          }}
                          disabled={isRejected ? !reviveStage : !nextMainStage}
                          title={isRejected
                            ? (reviveStage ? `复活候选人，回到「${STAGE_LABELS[reviveStage]}」阶段` : '无法复活')
                            : (nextMainStage ? `通过后进入「${STAGE_LABELS[nextMainStage]}」阶段` : '已到达最终阶段')}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 shadow-sm transition-colors disabled:opacity-40"
                        >
                          <Check size={14} />
                          {isRejected ? `复活→${reviveStage ? STAGE_LABELS[reviveStage] : ''}` : `通过${nextMainStage ? `→${STAGE_LABELS[nextMainStage]}` : ''}`}
                        </button>
                        <button
                          onClick={async () => {
                            if (!rejectStage) return;
                            if (rejectStage === 'offer_rejected') { setRejectOfferReason(''); setRejectOfferTarget({ navigate: true }); return; }
                            setStampAnimation('reject');
                            await handleStageChange(rejectStage);
                            setTimeout(() => {
                              setStampAnimation(null);
                              if (nextId && onNavigate) onNavigate(nextId);
                            }, 800);
                          }}
                          disabled={!rejectStage}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-900 bg-white border border-gray-900 hover:bg-gray-900 hover:text-white transition-colors disabled:opacity-40"
                        >
                          <X size={14} />
                          淘汰
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          if (!nextMainStage) return;
                          // 显示通过盖戳动画
                          setStampAnimation('pass');
                          // 更新阶段
                          await handleStageChange(nextMainStage);
                          // 等待动画完成后跳转到下一个候选人
                          setTimeout(() => {
                            setStampAnimation(null);
                            if (nextId && onNavigate) {
                              onNavigate(nextId);
                            }
                          }, 800);
                        }}
                        disabled={!nextMainStage}
                        title={nextMainStage ? `通过后进入「${STAGE_LABELS[nextMainStage]}」阶段` : '已到达最终阶段'}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 shadow-sm transition-colors disabled:opacity-40"
                      >
                        <Check size={14} />
                        通过{nextMainStage ? `→${STAGE_LABELS[nextMainStage]}` : ''}
                      </button>
                      <button
                        onClick={async () => {
                          // 根据当前阶段确定淘汰后的目标阶段
                          let rejectedStage = '';
                          if (candidate.stage === 'new' || candidate.stage === 'screening' || candidate.stage === 'screening_passed') {
                            rejectedStage = 'screening_rejected';
                          } else if (candidate.stage === 'first_interview') {
                            rejectedStage = 'first_interview_rejected';
                          } else if (candidate.stage === 'second_interview') {
                            rejectedStage = 'second_interview_rejected';
                          } else if (candidate.stage === 'final_interview') {
                            rejectedStage = 'final_interview_rejected';
                          } else if (candidate.stage === 'offer') {
                            rejectedStage = 'offer_rejected';
                          }
                          
                          if (!rejectedStage) return;
                          if (rejectedStage === 'offer_rejected') { setRejectOfferReason(''); setRejectOfferTarget({ navigate: true }); return; }
                          // 显示淘汰盖戳动画
                          setStampAnimation('reject');
                          // 更新阶段
                          await handleStageChange(rejectedStage);
                          // 等待动画完成后跳转到下一个候选人
                          setTimeout(() => {
                            setStampAnimation(null);
                            if (nextId && onNavigate) {
                              onNavigate(nextId);
                            }
                          }, 800);
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-900 bg-white border border-gray-900 hover:bg-gray-900 hover:text-white transition-colors"
                      >
                        <X size={14} />
                        淘汰
                      </button>
                    </div>
                  );
                })()}
              </div>

              {/* 发送飞书 / 发送邮件 —— 底部两个等大按钮，与「通过/淘汰」组成 2x2 */}
              {isHr && (
                <div className="flex gap-2" ref={actionMenuRef}>
                  {/* 发送飞书 */}
                  <div className="relative flex-1">
                    <button
                      onClick={() => { setShowEmailPicker(false); setShowFeishuPicker(!showFeishuPicker); if (!showFeishuPicker) loadFeishuInterviewers(); }}
                      disabled={sendingFeishu}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 hover:bg-primary-100 transition-colors disabled:opacity-50"
                    >
                      <Send size={14} />
                      发送飞书
                      <ChevronDown size={14} className="text-primary-400" />
                    </button>
                    {showFeishuPicker && (
                      <div className="absolute top-full left-0 mt-1 w-[calc(200%+0.5rem)] bg-white border border-gray-200 rounded-lg shadow-xl z-20 py-2 max-h-72 overflow-y-auto">
                        {/* 面试邀约（飞书通知） */}
                        <p className="px-3 text-[11px] font-semibold text-gray-500 mb-1.5">面试邀约 · 飞书通知</p>
                        <div className="px-3">
                          {feishuInterviewers.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-2">暂无已绑定飞书的面试官</p>
                          ) : (
                            <>
                              {(() => {
                                // 默认带出候选人所属岗位配置的面试官（仅保留已绑定飞书的），可一键发送；下方列表仍可手动选人
                                const posIds = positionInterviewerIds(positions, candidate.position_id);
                                const boundPosInterviewers = feishuInterviewers.filter(u => posIds.includes(u.id));
                                if (boundPosInterviewers.length === 0) return null;
                                return (
                                  <button
                                    onClick={() => handleSendFeishu(boundPosInterviewers.map(u => u.id))}
                                    disabled={sendingFeishu}
                                    className="w-full mb-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50"
                                  >
                                    <Send size={13} />一键通知岗位面试官（{boundPosInterviewers.length}人）
                                  </button>
                                );
                              })()}
                              <p className="text-[11px] text-gray-400 mb-2">选择面试官发送飞书通知：</p>
                              {feishuInterviewers.map(u => (
                                <button key={u.id}
                                  onClick={() => handleSendFeishu([u.id])}
                                  className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-primary-50 flex items-center gap-2 transition-colors"
                                >
                                  <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">
                                    {u.display_name?.charAt(0) || u.username.charAt(0)}
                                  </div>
                                  <span className="text-gray-700">{u.display_name || u.username}</span>
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 发送邮件 */}
                  <div className="relative flex-1">
                    <button
                      onClick={() => { setShowFeishuPicker(false); setShowEmailPicker(!showEmailPicker); }}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 hover:bg-primary-100 transition-colors"
                    >
                      <Mail size={14} />
                      发送邮件
                      <ChevronDown size={14} className="text-primary-400" />
                    </button>
                    {showEmailPicker && (
                      <div className="absolute top-full right-0 mt-1 w-[calc(200%+0.5rem)] bg-white border border-gray-200 rounded-lg shadow-xl z-20 py-2 max-h-72 overflow-y-auto">
                        <p className="px-3 text-[11px] font-semibold text-gray-500 mb-1.5">邮件通知 · 发送给候选人</p>
                        {!candidate.email?.trim() && (
                          <p className="px-3 pb-1.5 text-[11px] text-amber-600">该候选人未填写邮箱，无法发送</p>
                        )}
                        {([
                          { key: 'interview_invite', label: '面试邀约邮件', ready: true },
                          { key: 'rejection', label: '感谢 / 婉拒信', ready: true },
                          { key: 'offer_letter', label: 'Offer 发放邮件', ready: false },
                          { key: 'onboarding', label: '入职通知邮件', ready: false },
                        ] as const).map(m => (
                          <button
                            key={m.key}
                            onClick={() => m.ready ? openEmailModal(m.key as 'interview_invite' | 'rejection') : toast('该邮件类型即将上线')}
                            disabled={m.ready && !candidate.email?.trim()}
                            className={clsx(
                              'w-full text-left px-3 py-2 text-sm rounded-lg flex items-center gap-2 transition-colors',
                              m.ready
                                ? 'text-gray-700 hover:bg-primary-50 disabled:opacity-40 disabled:hover:bg-transparent'
                                : 'text-gray-500 hover:bg-gray-50'
                            )}
                          >
                            <Mail size={14} className={m.ready ? 'text-primary-400' : 'text-gray-400'} />
                            <span>{m.label}</span>
                            {!m.ready && <span className="ml-auto text-[10px] text-gray-300">敬请期待</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Rating slider - prominent (frosted-glass card lifts visual weight) */}
              <div className="rounded-xl bg-white/55 backdrop-blur-md border border-white/70 shadow-[0_6px_22px_-6px_rgba(46,107,240,0.18)] px-3.5 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-800">面试评分</span>
                  <span className="text-lg md:text-xl font-bold tabular-nums" style={{ color: evalRating > 0 ? getRatingTrackColor(evalRating) : '#9ca3af' }}>
                    {evalRating > 0 ? evalRating.toFixed(1) : '-'}
                  </span>
                </div>
                <input
                  type="range" min="0" max="100" step="1"
                  value={evalRating * 10}
                  onChange={e => setEvalRating(Math.round(parseInt(e.target.value)) / 10)}
                  className="w-full h-3 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: evalRating > 0
                      ? `linear-gradient(to right, ${getRatingTrackColor(evalRating)} 0%, ${getRatingTrackColor(evalRating)} ${evalRating * 10}%, #e5e7eb ${evalRating * 10}%, #e5e7eb 100%)`
                      : '#e5e7eb',
                  }}
                />
                {evalRating > 0 && (
                  <div className={clsx('mt-1.5 px-3 py-1 rounded-lg text-xs font-medium text-center', ratingLevel.bg, ratingLevel.color)}>
                    {ratingLevel.label}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT-BOTTOM: Tabs (备注 / 我的跟盯) + evaluation history */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Tabs */}
              <div className="flex items-center gap-0 px-5 pt-4 shrink-0">
                <button
                  onClick={() => setRightTab('notes')}
                  className={clsx(
                    'px-3 pb-2 text-sm font-medium border-b-2 transition-colors',
                    rightTab === 'notes' ? 'text-primary-600 border-primary-600' : 'text-gray-400 border-transparent hover:text-gray-600'
                  )}
                >
                  备注
                </button>
                <button
                  onClick={() => setRightTab('followup')}
                  className={clsx(
                    'px-3 pb-2 text-sm font-medium border-b-2 transition-colors',
                    rightTab === 'followup' ? 'text-primary-600 border-primary-600' : 'text-gray-400 border-transparent hover:text-gray-600'
                  )}
                >
                  我的跟盯
                </button>
              </div>
              <div className="border-b border-gray-200" />

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {rightTab === 'notes' && (
                  <div>
                    {/* Voice Input Section */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                          <Mic size={12} />语音面评
                        </span>
                        <div className="flex items-center gap-1.5">
                          {isRecording ? (
                            <button
                              onClick={stopVoiceRecording}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gray-500 hover:bg-gray-600 rounded-lg transition-colors animate-pulse"
                            >
                              <Square size={11} />停止
                            </button>
                          ) : (
                            <button
                              onClick={startVoiceRecording}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                            >
                              <Mic size={12} />开始录音
                            </button>
                          )}
                          {voiceTranscript.trim() && !isRecording && (
                            <button
                              onClick={handleAiGenerateEval}
                              disabled={aiGenerating}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {aiGenerating ? (
                                <><span className="animate-spin inline-block w-3 h-3 border border-primary-400 border-t-transparent rounded-full" />生成中...</>
                              ) : (
                                <><Sparkles size={12} />AI生成面评</>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      {(isRecording || voiceTranscript) && (
                        <div className="relative">
                          <textarea
                            value={voiceTranscript}
                            onChange={e => setVoiceTranscript(e.target.value)}
                            placeholder={isRecording ? '正在听你说...' : '语音内容会显示在这里，也可以手动编辑'}
                            rows={3}
                            className={clsx(
                              'w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none resize-none',
                              isRecording
                                ? 'border-gray-300 bg-gray-50/50 focus:ring-1 focus:ring-gray-400'
                                : 'border-gray-200 focus:ring-1 focus:ring-primary-500 focus:border-primary-500'
                            )}
                          />
                          {isRecording && (
                            <div className="absolute top-2 right-2 flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-gray-500 animate-pulse" />
                              <span className="text-[10px] text-gray-500 font-medium">录音中</span>
                            </div>
                          )}
                          {voiceTranscript && !isRecording && (
                            <button
                              onClick={() => setVoiceTranscript('')}
                              className="absolute top-2 right-2 p-0.5 text-gray-300 hover:text-gray-500 transition-colors"
                              title="清空"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <textarea
                        ref={evalTextareaRef}
                        value={evalComment}
                        onChange={handleEvalCommentChange}
                        onKeyDown={e => {
                          if (showMentionPicker && e.key === 'Escape') { setShowMentionPicker(false); e.preventDefault(); }
                        }}
                        onBlur={() => setTimeout(() => setShowMentionPicker(false), 200)}
                        placeholder="发表你的看法，输入 @ 可提醒同事..."
                        rows={4}
                        maxLength={1000}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
                      />
                      {showMentionPicker && mentionFilteredUsers.length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-2xl z-50 py-1.5 max-h-48 overflow-y-auto ring-1 ring-black/5">
                          <p className="text-[11px] text-gray-500 font-medium px-3 py-1.5 border-b border-gray-100">选择要@的同事</p>
                          {mentionFilteredUsers.map(u => (
                            <button
                              key={u.id}
                              onMouseDown={e => { e.preventDefault(); handleMentionSelect(u); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 flex items-center gap-2.5 transition-colors"
                            >
                              <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-[11px] font-bold text-primary-600 shrink-0">
                                {(u.display_name || u.username).charAt(0)}
                              </div>
                              <span className="text-gray-800 font-medium">{u.display_name || u.username}</span>
                              {u.feishu_open_id && <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded ml-auto">飞书已绑定</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      {parsedMentions.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                          <span className="text-[10px] text-gray-400">将提醒</span>
                          {parsedMentions.map(u => (
                            <span key={u.id} className="inline-flex items-center px-1.5 py-0.5 rounded bg-primary-50 text-primary-600 text-[10px]">
                              @{u.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] text-gray-400">{evalComment.length}/1000</span>
                        {isHr && (
                          <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={evalPrivate}
                              onChange={e => setEvalPrivate(e.target.checked)}
                              className="w-7 h-7 md:w-3.5 md:h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-[11px] text-gray-500">私密评价</span>
                          </label>
                        )}
                      </div>
                      <button
                        onClick={handleAddEvaluation}
                        disabled={!evalComment.trim()}
                        className="px-4 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        确定
                      </button>
                    </div>

                    {/* Evaluation history */}
                    <div className="mt-5">
                      <h4 className="text-xs font-medium text-gray-500 mb-3">讨论记录</h4>
                      <div className="space-y-3">
                        {candidate.evaluations?.map(ev => {
                          const evLevel = getRatingLevel(ev.rating);
                          const canDelete = ev.user_id === currentUser.id || isHr;
                          // 代交的面评以被代表的面试官为主体展示，代交人放到后面注明
                          const displayName = ev.on_behalf_of_name || ev.author_name;
                          const authorInitial = (displayName || '?').charAt(0);
                          return (
                            <div key={ev.id} className={clsx('border rounded-lg p-3', ev.is_private ? 'border-gray-200 bg-gray-50/30' : 'border-gray-100')}>
                              {/* Author row */}
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-[11px] font-bold text-primary-600 shrink-0">
                                  {authorInitial}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-semibold text-gray-700">{displayName || '未知'}</span>
                                    {ev.on_behalf_of_name && (
                                      <span className="text-[10px] text-gray-400">由 {ev.author_name} 代交</span>
                                    )}
                                    <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-medium text-white', stageColors[ev.stage] || 'bg-gray-400')}>
                                      {STAGE_LABELS[ev.stage as CandidateStage] || ev.stage}
                                    </span>
                                    {ev.rating > 0 && (
                                      <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-semibold', evLevel.color)}>
                                        {ev.rating.toFixed(1)}
                                      </span>
                                    )}
                                    {ev.is_private ? (
                                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">私密</span>
                                    ) : null}
                                  </div>
                                  <span className="text-[10px] text-gray-400">{formatBeijingTime(ev.created_at, 'datetime')}</span>
                                </div>
                                {canDelete && (
                                  <button
                                    onClick={() => handleDeleteEvaluation(ev.id)}
                                    className="p-1 text-gray-300 hover:text-gray-500 transition-colors shrink-0"
                                    title="删除评价"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </div>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap ml-9">{ev.comment}</p>
                            </div>
                          );
                        })}
                        {(!candidate.evaluations || candidate.evaluations.length === 0) && (
                          <p className="text-xs text-gray-400 text-center py-4">暂无讨论，快来发表第一条看法吧~</p>
                        )}
                      </div>
                    </div>

                    {/* Activity Log */}
                    {candidate.activity_logs && candidate.activity_logs.length > 0 && (
                      <div className="mt-5">
                        <h4 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                          <Calendar size={12} />操作记录
                        </h4>
                        <div className="space-y-1.5">
                          {candidate.activity_logs.map(log => (
                            <div key={log.id} className="flex items-start gap-2 text-[11px]">
                              <span className="text-gray-400 shrink-0">{formatBeijingTime(log.created_at, 'datetime')}</span>
                              {log.operator_name && <span className="text-primary-600 font-medium shrink-0">{log.operator_name}</span>}
                              <span className={log.detail && log.detail.includes('原因:') ? 'text-red-600' : 'text-gray-500'}>{log.detail}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {rightTab === 'followup' && (
                  <div>
                    {/* Candidate Notes - persistent field */}
                    <div className="mb-4">
                      <h4 className="text-xs font-medium text-gray-500 mb-2">跟盯备注</h4>
                      <textarea
                        key={`notes-${candidate.id}-${candidate.updated_at}`}
                        defaultValue={candidate.notes}
                        onBlur={e => {
                          if (e.target.value !== candidate.notes) handleFieldUpdate('notes', e.target.value);
                        }}
                        placeholder="记录跟进内容、下次联系时间等..."
                        rows={3}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
                      />
                    </div>

                    {/* Reminder Form */}
                    <div className="space-y-3">
                      <h4 className="text-xs font-medium text-gray-500 flex items-center gap-1">
                        <Clock size={12} />设置提醒
                      </h4>
                      <textarea
                        value={reminderContent}
                        onChange={e => setReminderContent(e.target.value)}
                        placeholder="提醒内容，如：联系候选人确认面试时间"
                        rows={2}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
                      />

                      <div className="flex gap-2">
                        <div className="flex-1">
                          <input
                            type="datetime-local"
                            value={reminderTime}
                            onChange={e => setReminderTime(e.target.value)}
                            className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <div className="flex-1">
                          <select
                            value={reminderTargetId}
                            onChange={e => setReminderTargetId(e.target.value)}
                            className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="">提醒谁</option>
                            {selectableUsers.map(u => (
                              <option key={u.id} value={u.id}>{u.display_name}{u.id === currentUser.id ? '（自己）' : ''}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <button
                        onClick={handleCreateReminder}
                        disabled={!reminderContent.trim() || !reminderTime || !reminderTargetId}
                        className="w-full text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg py-2.5 transition-colors"
                      >
                        确认设置
                      </button>
                    </div>

                    {/* Reminder History */}
                    <div className="mt-4">
                      <h4 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                        <Clock size={12} />提醒历史
                      </h4>
                      {reminders.length > 0 ? (
                        <div className="space-y-1.5">
                          {reminders.map(r => (
                            <div key={r.id} className={clsx(
                              'flex items-start gap-2 text-[11px] px-2.5 py-2 rounded-lg border',
                              r.status === 'sent' ? 'bg-gray-50 border-gray-100 opacity-60' : 'bg-gray-50/50 border-gray-100'
                            )}>
                              <Clock size={11} className={r.status === 'sent' ? 'text-gray-400 mt-0.5 shrink-0' : 'text-gray-500 mt-0.5 shrink-0'} />
                              <div className="flex-1 min-w-0">
                                <div className="text-gray-700">{r.content}</div>
                                <div className="text-gray-400 mt-0.5">
                                  {r.remind_at} · 提醒 {r.target_name}{r.creator_name ? ` · ${r.creator_name}设置` : ''}
                                  {r.status === 'sent' && <span className="ml-1 text-primary-500">✓ 已发送</span>}
                                </div>
                              </div>
                              {r.status === 'pending' && (
                                <button onClick={() => handleDeleteReminder(r.id)} className="text-gray-300 hover:text-gray-400 shrink-0 mt-0.5">
                                  <X size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] text-gray-300 text-center py-2">暂无提醒</p>
                      )}
                    </div>

                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interview code of conduct modal */}
      {showConductModal && (
        <InterviewCodeOfConductModal
          content={conductContent}
          onConfirm={() => {
            sessionStorage.setItem(SESSION_KEY, '1');
            setShowConductModal(false);
          }}
        />
      )}
      {/* 发送邮件确认弹窗（面试邀约 / 婉拒信） */}
      {emailModalType && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" onMouseDown={() => !sendingEmail && setEmailModalType(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl" onMouseDown={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Mail size={18} className="text-primary-500" />
              <h3 className="text-base font-semibold text-gray-800">
                {emailModalType === 'interview_invite' ? '发送面试邀约邮件' : '发送感谢 / 婉拒信'}
              </h3>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              收件人：<span className="font-medium text-gray-700">{candidate.name}</span>
              <span className="text-gray-400"> · {candidate.email}</span>
            </p>

            {emailModalType === 'interview_invite' ? (
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600 leading-relaxed">
                系统将向候选人邮箱发送<strong className="text-gray-800">最近一场已排期面试</strong>的通知（轮次、时间、方式与会议链接/地点）。若尚未排期，请先在「面试」中创建排期。
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">婉拒话术（留空则使用默认礼貌模板）</label>
                <textarea
                  value={emailMessage}
                  onChange={e => setEmailMessage(e.target.value)}
                  rows={7}
                  placeholder={`${candidate.name} 您好：\n\n非常感谢您应聘……（留空将自动使用默认礼貌婉拒话术）`}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                />
              </div>
            )}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => setEmailModalType(null)} disabled={sendingEmail}
                className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-50">
                取消
              </button>
              <button onClick={handleSendEmail} disabled={sendingEmail}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50">
                {sendingEmail ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                确认发送
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 拉分后确认：这条评分作为哪一轮面评提交 */}
      {claimPrompt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-gray-800">作为面评提交？</h3>
            <p className="mt-1.5 text-xs leading-relaxed text-gray-500">
              你打了 <span className="font-semibold text-primary-600">{evalRating.toFixed(1)}</span> 分。选择对应的面试轮次后，系统会按评分规则自动判定本轮结果；若只是随手记一笔，选「仅作为备注」。
            </p>
            <div className="mt-3 space-y-2">
              {claimPrompt.map((r: any) => (
                <label
                  key={r.id}
                  className={clsx(
                    'flex items-center gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors',
                    claimChoice === r.id ? 'border-primary-400 bg-primary-50/50' : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <input
                    type="radio"
                    checked={claimChoice === r.id}
                    onChange={() => {
                      setClaimChoice(r.id);
                      // 切轮次时重算默认身份，否则可能残留上一场的面试官
                      const meIsPending = (r.pendingInterviewers || []).some((iv: any) => iv.id === currentUser?.id);
                      setClaimBehalfOf(meIsPending ? '' : (r.pendingInterviewers?.[0]?.id || ''));
                    }}
                    className="w-5 h-5 md:w-3.5 md:h-3.5 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">{r.round || '面试'}</p>
                    <p className="text-[11px] text-gray-400">
                      {r.scheduled_time ? formatBeijingTime(r.scheduled_time, 'datetime') : '时间未定'}
                      {(r.interviewers || []).length > 1 ? ` · 共 ${r.interviewers.length} 位面试官` : ''}
                    </p>
                  </div>
                </label>
              ))}
            </div>
            {(() => {
              const sel = claimPrompt.find((r: any) => r.id === claimChoice);
              const pending = sel?.pendingInterviewers || [];
              const meIsPending = pending.some((iv: any) => iv.id === currentUser?.id);
              // 只有确实存在「可代的其他人」时才展示身份选择，避免单面试官场景白白多一行
              const others = pending.filter((iv: any) => iv.id !== currentUser?.id);
              if (!sel || others.length === 0) return null;
              return (
                <div className="mt-3">
                  <p className="text-[11px] font-medium text-gray-500">以谁的身份提交？</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {meIsPending && (
                      <button
                        onClick={() => setClaimBehalfOf('')}
                        className={clsx(
                          'rounded-full border px-2.5 py-1 text-[11px] transition-colors',
                          claimBehalfOf === ''
                            ? 'border-primary-400 bg-primary-50 text-primary-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        )}
                      >
                        我本人
                      </button>
                    )}
                    {others.map((iv: any) => (
                      <button
                        key={iv.id}
                        onClick={() => setClaimBehalfOf(iv.id)}
                        className={clsx(
                          'rounded-full border px-2.5 py-1 text-[11px] transition-colors',
                          claimBehalfOf === iv.id
                            ? 'border-primary-400 bg-primary-50 text-primary-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        )}
                      >
                        代 {iv.display_name || iv.name}
                      </button>
                    ))}
                  </div>
                  {claimBehalfOf && (
                    <p className="mt-1.5 text-[11px] text-gray-400">
                      这条面评会计在 TA 名下参与流转判定，记录里会注明由你代交。
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-[11px] leading-relaxed text-gray-700">
                判定规则：全部面试官均 ≥7 分 → 通过并进入下一轮；最低分在 6~7 分 → 本轮待定；任一面试官低于 6 分 → 直接一票否决淘汰。
              </p>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={() => submitEvaluation(claimChoice, claimBehalfOf || undefined)}
                disabled={!claimChoice}
                className="flex-1 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40"
              >
                {claimBehalfOf ? '代他提交面评' : '提交面评'}
              </button>
              <button
                onClick={() => submitEvaluation(null)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                仅作为备注
              </button>
              <button
                onClick={() => { setClaimPrompt(null); setClaimChoice(''); setClaimBehalfOf(''); }}
                className="rounded-lg px-3 py-2 text-sm text-gray-400 hover:bg-gray-50"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 取消面试：填写取消原因备注，通知面试官 */}
      {cancelIvId && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCancelIvId(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900">取消面试</h3>
            <p className="mt-1 text-sm text-gray-500">确定要取消这场面试吗？将同时删除飞书日程并通知面试官。</p>
            <label className="mt-4 block text-sm font-medium text-gray-700">
              取消原因 <span className="text-gray-400 font-normal">（选填，将随通知发给面试官）</span>
            </label>
            <textarea
              autoFocus
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              maxLength={200}
              placeholder="例如：面试官临时出差，改期另行安排…"
              className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setCancelIvId(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                再想想
              </button>
              <button
                onClick={confirmCancelInterview}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                确认取消面试
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 拒Offer：填写拒绝原因备注，记入操作记录供后续同学查看 */}
      {rejectOfferTarget && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setRejectOfferTarget(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900">标记为「拒Offer」</h3>
            <p className="mt-1 text-sm text-gray-500">确认将该候选人阶段调整为「拒Offer」？</p>
            <label className="mt-4 block text-sm font-medium text-gray-700">
              拒绝原因 <span className="text-gray-400 font-normal">（选填，将记入操作记录供后续同学查看）</span>
            </label>
            <textarea
              autoFocus
              value={rejectOfferReason}
              onChange={(e) => setRejectOfferReason(e.target.value)}
              rows={3}
              maxLength={200}
              placeholder="例如：已接受其他公司 offer / 薪资未谈拢 / 因个人原因放弃…"
              className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setRejectOfferTarget(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                再想想
              </button>
              <button
                onClick={confirmRejectOffer}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                确认标记拒Offer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
